<template>
  <!-- ==============================================================
       GuideTourBuilder — ADMIN authoring tool for the live tour.

       A clearer alternative to the mic recorder: a docked panel where
       the admin builds the tour STEP BY STEP — type the narration
       (spoken aloud via the browser; upgradeable to the nova voice
       later), pick the element to spotlight, and mark any extra
       affected sections. The REAL page is the live preview: hit ▶ on a
       step to see it spotlight + hear the text exactly as a customer
       will. Saves to localStorage (prototype) for GuideTourWidget to
       play back. Launched by the root event 'open-tour-builder'.
       Mounted once in App.vue. CSS unscoped + namespaced (.gtb-*).
       ============================================================== -->
  <div class="guide-tour-builder" v-if="open">
    <!-- Multi-hole page dim (edit view): strips tiled around every highlight so all
         of them punch through, not just the adjustable box. -->
    <div v-for="(d, i) in editDimRects" :key="'edim' + i" class="gtb-editdim" :style="d"></div>
    <!-- spotlight shown briefly during a per-step preview -->
    <div class="gtb-spot" v-if="spot" :style="spot"></div>
    <div v-for="(r, i) in ringStyles" :key="'rg' + i" class="gtb-ring"
         :class="[r.hi != null ? 'is-clickable' : '', r.style.anim && r.style.anim !== 'none' ? 'gtb-anim-' + r.style.anim : '']" :style="r.style"
         :title="r.hi != null ? 'Click to move / resize this highlight' : null"
         @mousedown.prevent.stop="editRing(r.hi)"></div>
    <!-- LIVE on-page preview while the highlight STYLE box is open — the element(s)
         outlined with the style currently being edited, updating as the controls change. -->
    <div v-for="(m, i) in styleEditPreview" :key="'sp' + i" class="gtb-stylepreview"
         :class="m.anim && m.anim !== 'none' ? 'gtb-anim-' + m.anim : ''" :style="m"></div>
    <!-- Page outlines drawn when a PANEL chip is clicked → locate a highlight/block on
         the live page WITHOUT opening Preview/Resize (red = highlight, green = block). -->
    <div v-for="(m, i) in shownPanelMarks" :key="'pm' + i" class="gtb-pagemark"
         :class="{ 'is-block': panelMarkKind === 'mask' }" :style="m"></div>
    <!-- Click-block regions for the step being edited: faint by default, GREEN
         when their chip is clicked, so the admin can see what each block hides. -->
    <div v-for="(m, i) in maskStyles" :key="'mk' + i" class="gtb-maskpreview" :class="{ 'is-active': m.active }" :style="m.style"></div>

    <!-- PICK mode: panel hides so the whole page is clickable -->
    <div v-if="pick" class="gtb-pickbar">
      🎯 Click the {{ pick.kind === 'target' ? 'control to spotlight' : pick.kind === 'mask' ? 'element to BLOCK from clicking' : 'extra area to mark' }} on the page…
      <button type="button" class="gtb-mini" @click="cancelPick">Cancel</button>
    </div>

    <!-- ADJUST/PREVIEW mode: the picked box stays put so the admin can
         move/resize it; a card shows the read-aloud narration + controls. -->
    <div v-if="editing && editing.box" class="gtb-edit" :class="{ 'gtb-edit-picking': pick }" :style="editStyle" @mousedown.self.prevent="startMove" title="Drag anywhere on the box to move the highlight">
      <div class="gtb-edit-move" @mousedown.stop.prevent="startMove" title="Drag to move the highlight">✥</div>
      <div class="gtb-edit-del" @mousedown.stop.prevent @click.stop="deleteEditingHighlight" title="Delete this highlight">✕</div>
      <div class="gtb-edit-handle" @mousedown.stop.prevent="startResize" title="Drag to resize"></div>
    </div>
    <div v-if="editing" class="gtb-edit-card" :style="editCardStyle">
      <div class="gtb-edit-card-step gtb-card-grip" @mousedown.stop.prevent="startCardMove" title="Drag to move this card">✥ Step {{ editing.idx + 1 }}<template v-if="steps[editing.idx] && steps[editing.idx].name"> · {{ steps[editing.idx].name }}</template></div>
      <textarea class="gtb-edit-card-input" v-model="editing.text" rows="3"
                placeholder="Type the narration for this step… (**bold**, _italic_, new lines)"></textarea>
      <!-- Record voice directly to this step (alternative/﻿addition to typing). -->
      <div class="gtb-edit-card-mic">
        <button v-if="!recording" type="button" class="gtb-mic-btn" @click="startRecord">
          🎤 {{ editing.audio ? "Re-record voice" : "Record voice" }}
        </button>
        <button v-else type="button" class="gtb-mic-btn is-rec" @click="stopRecord">● Stop</button>
        <span v-if="editing.audio && !recording" class="gtb-mic-has">auto voice ✓ · Remove to refresh</span>
        <button v-if="editing.audio && !recording" type="button" class="gtb-mic-x" @click="editing.audio = null">remove</button>
      </div>
      <!-- Add EXTRA highlights (dashed outline rings) on other elements this step. -->
      <div class="gtb-edit-masks">
        <button type="button" class="gtb-edit-maskbtn" @click="startPick(editing.idx, 'highlight')">✨ Add another highlight…</button>
        <span v-for="(h, hi) in ((steps[editing.idx] && steps[editing.idx].highlights) || [])" :key="'eh' + hi"
              class="gtb-mask-chip gtb-mask-chip--click" :class="{ 'gtb-hl-active': editing.areaIndex === hi }"
              @click="editRing(hi)" title="Click to select this highlight on the page (then move / resize it)">
          ✨ {{ h.label || h.selector }}<span v-if="hlMatchCount(h.selector) !== 1" class="gtb-hl-count" :class="{ 'is-zero': hlMatchCount(h.selector) === 0 }"
                :title="hlMatchCount(h.selector) === 0 ? 'Not on this page right now (0 matches).' : 'This selector matches ' + hlMatchCount(h.selector) + ' elements, so it paints a box on EACH — delete it (✕) and re-pick a single element.'">×{{ hlMatchCount(h.selector) }}</span>
          <button type="button" class="gtb-style-btn" :class="{ 'is-on': styleEdit && styleEdit.i === editing.idx && styleEdit.hi === hi }" @click.stop="openStyleEdit(editing.idx, hi)" title="Style this highlight — color, border, thickness (with live preview)">🎨</button>
          <button type="button" class="gtb-mask-x" @click.stop="removeHighlight(editing.idx, hi)" title="Remove this highlight">✕</button>
        </span>
      </div>
      <!-- Block clicks on page elements during this step (e.g. the cart icon). -->
      <div class="gtb-edit-masks">
        <button type="button" class="gtb-edit-maskbtn" @click="startPick(editing.idx, 'mask')">🚫 Block clicks on an element…</button>
        <span v-for="(m, mi) in ((steps[editing.idx] && steps[editing.idx].masks) || [])" :key="'em' + mi"
              class="gtb-mask-chip gtb-mask-chip--click" :class="{ 'is-active': activeMaskSel === m.selector }"
              @click="toggleMaskHighlight(m.selector)" title="Click to outline what this block hides (green)">
          🚫 {{ m.label || m.selector }}
          <button type="button" class="gtb-mask-x" @click.stop="removeMask(editing.idx, mi)" title="Remove this block">✕</button>
        </span>
      </div>
      <div class="gtb-edit-warn" v-if="editing.targetMissing">⚠ This step's highlight isn't on the page yet — narration &amp; masks are editable now. Select the machine/assembly so the element appears and the box will <b>snap on automatically</b> (no need to Preview again).</div>
      <div class="gtb-edit-card-hint" v-else>Drag the red box to move it · the corner dot resizes it · click another red highlight to move/resize that one · drag this card's ✥ Step header to move the card.</div>
      <div class="gtb-edit-card-row">
        <!-- Wildcard-token reference: hover (or focus) to see what's available. -->
        <span class="gtb-token-help" tabindex="0" role="button" aria-label="Available wildcard tokens">
          <span class="gtb-token-ico">{ }</span>
          <span class="gtb-token-pop" v-html="tokenHelpHtml"></span>
        </span>
        <button type="button" class="gtb-edit-cancel" @click="cancelEdit">Cancel</button>
        <span class="gtb-spacer"></span>
        <button type="button" class="gtb-edit-replay" @click="replayEdit">🔊 Play</button>
        <button type="button" class="gtb-edit-ok" @click="okEdit">OK</button>
        <button type="button" class="gtb-edit-next" v-if="editing.idx < steps.length - 1" @click="nextEdit">Next ▸</button>
      </div>
    </div>

    <!-- Recorded-tour REVIEW modal: watch the capture, then Save or Discard.
         Viewport-level overlay (sibling of the docked panel) so it isn't clipped. -->
    <div v-if="pendingRec" class="gtb-recmodal">
      <div class="gtb-recmodal-box">
        <div class="gtb-recmodal-h">Review your recorded “{{ pendingRec.label }}” tour</div>
        <div class="gtb-recmodal-sub">Watch it below, then decide whether to save it as this page’s guide video.</div>
        <video class="gtb-recmodal-vid" :src="pendingRec.url" controls autoplay playsinline></video>
        <div class="gtb-recmodal-meta">{{ pendingRec.mb }} MB · {{ pendingRec.fmt }} — saving replaces any current video for this page.</div>
        <div class="gtb-recmodal-acts">
          <button type="button" class="gtb-recmodal-save" :disabled="pendingRec.saving" @click="confirmSaveRecording">{{ pendingRec.saving ? "Saving…" : "✓ Save as guide video" }}</button>
          <button type="button" class="gtb-recmodal-discard" :disabled="pendingRec.saving" @click="discardRecording">✕ Discard</button>
        </div>
      </div>
    </div>

    <!-- EXPORT picker — choose WHICH steps to export (all checked by default). -->
    <div v-if="ieExport" class="gtb-iemodal" @click.self="ieExport = null">
      <div class="gtb-iemodal-box">
        <div class="gtb-recmodal-h">Export steps from “{{ flowLabel }}”</div>
        <div class="gtb-recmodal-sub">Pick the steps to export, then open the other tour, click Import, and choose what each one adds or replaces.</div>
        <div class="gtb-ie-toolbar">
          <button type="button" class="gtb-ie-selall" @click="ieToggleAll(!ieAllSelected)">{{ ieAllSelected ? "Deselect all" : "Select all" }}</button>
          <span class="gtb-ie-count">{{ ieExportCount }} of {{ savableSteps.length }} selected</span>
        </div>
        <div class="gtb-ie-list">
          <div class="gtb-ie-head">
            <span></span>
            <span>#</span>
            <span>Step narration</span>
          </div>
          <label class="gtb-ie-row" v-for="(s, i) in savableSteps" :key="'exp' + i">
            <input type="checkbox" :checked="ieExport.sel[i]" @change="$set(ieExport.sel, i, $event.target.checked)" />
            <span class="gtb-ie-num">{{ i + 1 }}</span>
            <span class="gtb-ie-text" :title="s.text || ''">{{ stepSummary(s) }}</span>
          </label>
        </div>
        <div class="gtb-recmodal-acts">
          <button type="button" class="gtb-recmodal-save gtb-ie-go" :disabled="ieExportCount === 0" @click="doExport">⬇ Export {{ ieExportCount }} step{{ ieExportCount === 1 ? '' : 's' }}</button>
          <button type="button" class="gtb-recmodal-discard" @click="ieExport = null">Cancel</button>
        </div>
      </div>
    </div>

    <!-- IMPORT mapping — per incoming step, ADD as new or REPLACE a chosen existing step. -->
    <div v-if="ieImport" class="gtb-iemodal" @click.self="ieImport = null">
      <div class="gtb-iemodal-box">
        <div class="gtb-recmodal-h">Import into “{{ flowLabel }}”</div>
        <div class="gtb-recmodal-sub">{{ ieImport.rows.length }} step(s) from <b>{{ ieImport.name }}</b><span v-if="ieImport.flow && ieImport.flow !== flowKey"> (from the “{{ ieImport.flow }}” tour)</span>. The green badge is each step’s <b>original number</b>; the dropdown defaults to replacing the same-numbered step here. Hover a row to read its full text.</div>
        <div class="gtb-ie-list">
          <div class="gtb-ie-maprow" v-for="(r, i) in ieImport.rows" :key="'imp' + i">
            <div class="gtb-ie-inc">
              <span class="gtb-ie-num gtb-ie-num--in" :title="'original step ' + (r.step.__srcNum || (i + 1)) + (ieImport.flow ? ' from “' + ieImport.flow + '”' : '')">{{ r.step.__srcNum || (i + 1) }}</span>
              <span class="gtb-ie-text" :title="r.step.text || ''">{{ stepSummary(r.step) }}</span>
            </div>
            <select class="gtb-showif-sel gtb-ie-sel" v-model="r.map">
              <option value="">➕ Add as a new step</option>
              <optgroup label="Replace an existing step" v-if="savableSteps.length">
                <option v-for="(s, j) in savableSteps" :key="'rep' + i + '-' + j" :value="steps.indexOf(s)">↻ Replace step {{ j + 1 }}: {{ stepSummary(s) }}</option>
              </optgroup>
            </select>
          </div>
        </div>
        <div class="gtb-recmodal-acts">
          <button type="button" class="gtb-recmodal-save gtb-ie-go" @click="applyImport">Apply import</button>
          <button type="button" class="gtb-recmodal-discard" @click="ieImport = null">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Highlight STYLE box (root-level floating panel so it shows from BOTH the panel
         and the Preview/Resize card). Presets + border/fill color + thickness + line,
         with a LIVE on-page preview — no need to open Preview. -->
    <div v-if="styleEdit" class="gtb-styleedit" :style="styleEditStyle">
      <div class="gtb-styleedit-head" @mousedown.prevent="startStyleMove" title="Drag to move this box">
        <span>🎨 Highlight style</span>
        <button type="button" class="gtb-mini" @mousedown.stop @click="closeStyleEdit">✓ Done</button>
      </div>
      <div class="gtb-style-body">
        <div class="gtb-style-presets">
          <button v-for="p in stylePresets" :key="p.name" type="button" class="gtb-style-preset" @click="applyStylePreset(p)">{{ p.name }}</button>
        </div>
        <div class="gtb-style-row">
          <span class="gtb-style-lbl">Border color</span>
          <input type="color" class="gtb-style-color" :value="curStyle.border || '#c0392b'" @input="setStyle('border', $event.target.value)" />
        </div>
        <div class="gtb-style-row">
          <span class="gtb-style-lbl">Fill color</span>
          <input type="color" class="gtb-style-color" :value="curStyle.bg || '#ffeb3b'" @input="setStyle('bg', $event.target.value)" />
          <button type="button" class="gtb-mini" :class="{ 'is-on': !curStyle.bg }" @click="setStyle('bg', '')" title="No fill (transparent)">No fill</button>
        </div>
        <div class="gtb-style-row">
          <span class="gtb-style-lbl">Thickness</span>
          <input type="range" class="gtb-style-range" min="0" max="8" step="1" :value="curStyle.width" @input="setStyle('width', Number($event.target.value))" />
          <span class="gtb-style-val">{{ curStyle.width }}px</span>
        </div>
        <div class="gtb-style-row">
          <span class="gtb-style-lbl">Line</span>
          <button v-for="ln in ['solid','dashed','dotted']" :key="ln" type="button" class="gtb-style-line" :class="{ 'is-on': curStyle.line === ln }" @click="setStyle('line', ln)">{{ ln }}</button>
        </div>
        <div class="gtb-style-anim-row">
          <span class="gtb-style-lbl">Animation</span>
          <div class="gtb-style-anims">
            <button v-for="a in ['none','glow','sonar','pulse','blink','bounce','shake']" :key="a" type="button" class="gtb-style-line" :class="{ 'is-on': curStyle.anim === a }" @click="setStyle('anim', a)">{{ a }}</button>
          </div>
        </div>
        <div class="gtb-style-row" v-if="curStyle.anim !== 'none'">
          <span class="gtb-style-lbl">Run</span>
          <input type="number" class="gtb-style-num" min="1" max="99"
                 :value="curStyle.animCount === 'infinite' ? '' : curStyle.animCount" placeholder="#"
                 @input="setStyle('animCount', Math.max(1, Math.min(99, Number($event.target.value) || 1)))" />
          <span class="gtb-style-times">times</span>
          <button type="button" class="gtb-style-line" :class="{ 'is-on': curStyle.animCount === 'infinite' }" @click="setStyle('animCount', 'infinite')">All the time</button>
        </div>
        <div class="gtb-style-hint">The element is outlined live on the page as you adjust — no need to open Preview.</div>
      </div>
    </div>

    <!-- BUILDER panel -->
    <div class="gtb-panel" v-show="!pick && !editing">
      <div class="gtb-head">
        <span class="gtb-title">Tour Builder — {{ flowLabel }}</span>
        <button type="button" class="gtb-x" @click="close" aria-label="Close">✕</button>
      </div>
      <div class="gtb-submeta">
        <span class="gtb-ver">{{ serverVersion ? "Saved: v" + serverVersion : "Not saved yet" }}</span>
        <span class="gtb-dot">·</span>
        <span class="gtb-cnt">{{ savableSteps.length }} {{ savableSteps.length === 1 ? "step" : "steps" }}</span>
        <span class="gtb-dot">·</span>
        <label class="gtb-voicewrap" title="Narration voice (Azure Neural)">🔊
          <select class="gtb-voice" v-model="voice" @change="onVoiceChange">
            <option v-for="v in voiceOptions" :key="v.id" :value="v.id">{{ v.label }}</option>
          </select>
        </label>
      </div>

      <div class="gtb-steps">
        <div v-for="(s, i) in steps" :key="i" class="gtb-step">
          <div class="gtb-step-top">
            <span class="gtb-step-n">Step {{ i + 1 }}</span>
            <input type="text" class="gtb-step-name" v-model="s.name"
                   placeholder="name this step (optional)"
                   title="A short label for this step — admin only, for your organization" />
            <span class="gtb-step-tools">
              <button type="button" class="gtb-mini" :disabled="i === 0" @click="move(i, -1)" title="Move up">▲</button>
              <button type="button" class="gtb-mini" :disabled="i === steps.length - 1" @click="move(i, 1)" title="Move down">▼</button>
              <button type="button" class="gtb-mini gtb-ins" @click="openInsertPicker(i)" title="Insert a step after this one — blank, or a copy of an existing step">＋</button>
              <button type="button" class="gtb-mini gtb-del" @click="removeStep(i)" title="Delete">✕</button>
            </span>
          </div>

          <textarea class="gtb-text" v-model="s.text" rows="2"
                    placeholder="Type what to say… (**bold**, _italic_, new lines, {wait})"></textarea>
          <!-- {wait}: the voice pauses at this point until the customer changes the
               highlighted field (or clicks Skip), then speaks the rest. -->
          <div class="gtb-wait-hint" v-if="s.text && /\{wait\}/i.test(s.text)">
            ⏸ <b>{wait}</b> pauses the voice here until the customer changes the <b>first highlight</b> (e.g. the quantity) or clicks <b>Skip</b> — then it speaks the rest. A Skip/Next button shows automatically.
          </div>
          <!-- live render of the markup the customer will see on the card -->
          <div class="gtb-rendered" v-if="s.text && s.text.trim()" v-html="narrationPreview(s.text)"></div>

          <div class="gtb-step-row">
            <button type="button" class="gtb-pickbtn" @click="startPick(i, 'target')">
              🎯 {{ s.targetLabel ? "Highlight: " + s.targetLabel : "Pick the element to highlight" }}
            </button>
            <button v-if="s.target" type="button" class="gtb-eye" :class="{ 'is-active': panelMarkKey === 'eye:' + i }"
                    @click="markOnPage('highlight', s.target, 'eye:' + i)" title="Show this highlight on the page">👁</button>
            <button v-if="s.target" type="button" class="gtb-eye gtb-eye-del" @click="clearTarget(i)" title="Remove this highlight (clears the 🎯 target)">✕</button>
          </div>
          <!-- Secondary highlights (✨) and blocks (🚫) are managed inside Preview / Resize
               now — the first page stays clean with just the main 🎯 highlight. This muted
               summary only signals they exist (and a ⚠ if a highlight selector is broad /
               missing) and jumps straight into the editor where the chips live. -->
          <button type="button" class="gtb-extras-summary"
                  v-if="(s.highlights && s.highlights.length) || (s.masks && s.masks.length)"
                  @click="previewStep(i)" :disabled="!s.text && !s.target"
                  title="Open Preview / Resize to view, move, style, or remove these">
            <span v-if="s.highlights && s.highlights.length" class="gtb-extras-h">✨ {{ s.highlights.length }} highlight{{ s.highlights.length === 1 ? '' : 's' }}<span
                  v-if="stepHlWarn(s)" class="gtb-extras-warn" title="One or more highlights don't match exactly one element (broad or missing selector) — open Preview / Resize to fix.">⚠</span></span>
            <span v-if="s.masks && s.masks.length" class="gtb-extras-b">🚫 {{ s.masks.length }} block{{ s.masks.length === 1 ? '' : 's' }}</span>
            <span class="gtb-extras-cta">edit in Preview / Resize ›</span>
          </button>

          <div class="gtb-step-actions">
            <button type="button" class="gtb-play" @click="previewStep(i)" :disabled="!s.text && !s.target">▶ Preview / resize</button>
            <label class="gtb-droptoggle" title="Dropdown/select: the customer opens it and picks a value before the tour advances (needs two interactions)">
              <input type="checkbox" :checked="s.type === 'dropdown'" @change="setDropdown(s, $event.target.checked)" />
              <span>Dropdown step</span>
            </label>
            <label class="gtb-droptoggle" title="Input/search field: the customer can type into it (typing won't close the step); the highlight then extends to the results below. Pair with the Next button to continue.">
              <input type="checkbox" :checked="s.type === 'input'" @change="setInput(s, $event.target.checked)" />
              <span>Input field</span>
            </label>
            <label class="gtb-droptoggle" title="Cart (auto-expand): the highlight wraps the WHOLE floating cart and grows live as the customer adds parts/quotes (the 'Your Quotes In Progress' section), so nothing gets cut off. Point this step's 🎯 highlight anywhere on the cart, then pair with the Next button.">
              <input type="checkbox" :checked="s.type === 'cart'" @change="setCart(s, $event.target.checked)" />
              <span>Cart (auto-expand)</span>
            </label>
            <label class="gtb-droptoggle" title="Show a Next button on this step (for informational steps with no action to click). Off = the customer advances by doing the highlighted action.">
              <input type="checkbox" :checked="!!s.showNext" @change="setShowNext(s, $event.target.checked)" />
              <span>Next button</span>
            </label>
            <label class="gtb-droptoggle gtb-showif" title="Conditionally SHOW this step. 'If a drawing is shown' / 'If NO drawing' auto-detects whether the selected assembly has a drawing on screen, and the tour skips this step when it doesn't match — so you can have one step for 'has drawing' and another for 'no drawing'.">
              <span>Show:</span>
              <select class="gtb-showif-sel" :value="stepShowIf(s)" @change="setStepShowIf(s, $event.target.value)">
                <option value="">Always</option>
                <optgroup label="Assembly drawing">
                  <option value="hasdrawing">If a drawing is shown</option>
                  <option value="nodrawing">If NO drawing</option>
                </optgroup>
                <optgroup label="Machine assemblies">
                  <option value="hasassembly">If the machine has assemblies</option>
                  <option value="noassembly">If NO assemblies</option>
                </optgroup>
              </select>
            </label>
          </div>

          <!-- Input-step only: a message shown once the customer types + narrows
               the results. {part} is replaced with the top matching part code. -->
          <div class="gtb-result-row" v-if="s.type === 'input'">
            <label class="gtb-result-label">Result callout
              <span class="gtb-result-hint">shown after they filter · use <code>{part}</code> for the matched part code</span>
            </label>
            <input type="text" class="gtb-result-input" v-model="s.resultMsg"
                   placeholder="e.g. Great! You've narrowed it down to {part}." />
          </div>
        </div>

        <button type="button" class="gtb-addstep" @click="addStep">＋ Add step</button>
      </div>

      <div class="gtb-foot">
        <button type="button" class="gtb-ghost" @click="startOver">Start over</button>
        <button type="button" class="gtb-ghost" @click="openVersions" title="View / restore older saved versions">↺ Versions</button>
        <select v-if="copyOptions.length" v-model="copySource" class="gtb-copy-sel"
                title="Copy another tour's steps into this one to tweak">
          <option value="">⧉ copy from…</option>
          <option v-for="o in copyOptions" :key="o.flow" :value="o.flow">{{ o.label }}</option>
        </select>
        <button v-if="copyOptions.length" type="button" class="gtb-ghost" @click="copyFromTour" :disabled="!copySource"
                title="Replace these steps with a copy of the selected tour (you can tweak before saving)">Copy</button>
        <button type="button" class="gtb-ghost" @click="openStartPicker" :disabled="!hasContent">▶ Preview tour</button>
        <button type="button" class="gtb-ghost" @click="openExport" :disabled="!hasContent"
                title="Export chosen steps as a JSON file — to move them to another tour or environment">⬇ Export</button>
        <button type="button" class="gtb-ghost" @click="$refs.importFile.click()"
                title="Import steps from a JSON file — you choose what each one adds or replaces, then Save to publish here">⬆ Import</button>
        <input ref="importFile" type="file" accept="application/json,.json" class="gtb-import-input" @change="importTour" />
        <span class="gtb-spacer"></span>
        <button type="button" class="gtb-save" @click="save" :disabled="!hasContent || saving">{{ saving ? "Saving…" : "Save tour" }}</button>
      </div>
      <div class="gtb-note" :class="'gtb-note--' + (noteKind || 'info')" v-if="note">{{ note }}</div>

      <!-- Recorded-tour save receipt: stays until dismissed so the admin can read
           (and copy) exactly where the recording was stored. -->
      <div class="gtb-saved" v-if="savedInfo">
        <div class="gtb-saved-h">✓ Recording saved as the “{{ savedInfo.label }}” guide video</div>
        <div class="gtb-saved-row"><span>Size</span><code>{{ savedInfo.mb }} MB · {{ savedInfo.format }}</code></div>
        <div class="gtb-saved-row"><span>Saved to</span><code>{{ savedInfo.path }}</code></div>
        <div class="gtb-saved-row" v-if="savedInfo.url"><span>Plays from</span><code>{{ savedInfo.url }}</code></div>
        <div class="gtb-saved-acts">
          <a v-if="savedInfo.url" :href="savedInfo.url" target="_blank" rel="noopener" class="gtb-saved-link">▶ Open the saved video</a>
          <button type="button" class="gtb-saved-x" @click="savedInfo = null">Dismiss</button>
        </div>
      </div>

      <!-- Preview tour: choose whether to record, then pick a starting step. -->
      <div v-if="showStartPicker" class="gtb-versions">
        <div class="gtb-versions-head">
          <span>Preview tour</span>
          <button type="button" class="gtb-mini" @click="showStartPicker = false" aria-label="Close">✕</button>
        </div>

        <!-- Record-this-run options. Recording the run uploads it as the guide
             video customers watch for this flow. -->
        <div class="gtb-rec-opts" v-if="canRecord">
          <div class="gtb-rec-title">📹 Record this run as the “{{ flowLabel }}” guide video?</div>
          <label class="gtb-rec-row"><input type="radio" value="off" v-model="recordMode"> <span>No — just rehearse</span></label>
          <label class="gtb-rec-row"><input type="radio" value="audio" v-model="recordMode"> <span>Record the tab <b>with</b> audio (narration is captured)</span></label>
          <label class="gtb-rec-row"><input type="radio" value="noaudio" v-model="recordMode"> <span>Record the tab <b>without</b> audio</span></label>
          <div class="gtb-rec-hint" v-if="recordMode !== 'off'">
            Your browser will ask to share <b>this tab</b> — just click <b>Share</b>{{ recordMode === 'audio' ? " (leave “Also share tab audio” on so the narration is captured)" : "" }}. The recording saves automatically as the guide video when the tour ends.
          </div>
        </div>
        <div class="gtb-rec-opts gtb-rec-opts--off" v-else>
          📹 Screen recording isn’t available in this browser — you can still rehearse. (Use desktop Chrome or Edge to record.)
        </div>

        <div class="gtb-versions-head gtb-versions-head--sub"><span>Start at which step?</span></div>
        <div class="gtb-versions-list">
          <div v-for="(s, i) in savableSteps" :key="'sp' + i" class="gtb-version-row">
            <div class="gtb-version-meta">
              <span class="gtb-version-n">Step {{ i + 1 }}<template v-if="s.name"> · {{ s.name }}</template></span>
              <span class="gtb-version-sub">{{ stepSnippet(s) }}</span>
            </div>
            <button type="button" class="gtb-mini" @click="previewFrom(i)">{{ recordMode === 'off' ? '▶ Start here' : '⏺ Record from here' }}</button>
          </div>
        </div>
      </div>

      <!-- Version history: review/restore an older saved version. -->
      <div v-if="showVersions" class="gtb-versions">
        <div class="gtb-versions-head">
          <span>Saved versions — {{ flowLabel }}</span>
          <button type="button" class="gtb-mini" @click="showVersions = false" aria-label="Close">✕</button>
        </div>
        <div v-if="versionsLoading" class="gtb-versions-empty">Loading…</div>
        <div v-else-if="!versionList.length" class="gtb-versions-empty">No saved versions yet.</div>
        <div v-else class="gtb-versions-list">
          <div v-for="v in versionList" :key="v.version" class="gtb-version-row">
            <div class="gtb-version-meta">
              <span class="gtb-version-n">v{{ v.version }}</span>
              <span v-if="v.active" class="gtb-version-active">current</span>
              <span class="gtb-version-sub">{{ v.stepCount }} {{ v.stepCount === 1 ? "step" : "steps" }} · {{ fmtDate(v.createdAtUtc) }}<template v-if="v.createdBy"> · {{ v.createdBy }}</template></span>
            </div>
            <button type="button" class="gtb-mini" @click="loadVersion(v)">Load</button>
          </div>
        </div>
        <div class="gtb-versions-foot">Load a version to review it here, then <b>Save tour</b> to make it the current one.</div>
      </div>

      <!-- Insert a step (＋ on a step): a blank one, OR a copy of an existing step
           dropped in right after the step the ＋ was pressed on. -->
      <div v-if="insertPicker.open" class="gtb-versions">
        <div class="gtb-versions-head">
          <span>Insert a step after Step {{ insertPicker.after + 1 }}</span>
          <button type="button" class="gtb-mini" @click="insertPicker.open = false" aria-label="Close">✕</button>
        </div>
        <button type="button" class="gtb-addstep gtb-ins-blank" @click="insertBlankAt()">＋ Blank new step</button>
        <div class="gtb-versions-head gtb-versions-head--sub"><span>…or reuse a copy of an existing step</span></div>
        <div v-if="!reusableSteps.length" class="gtb-versions-empty">No steps with content to copy yet.</div>
        <div v-else class="gtb-versions-list">
          <div v-for="row in reusableSteps" :key="'ins' + row.i" class="gtb-version-row">
            <div class="gtb-version-meta">
              <span class="gtb-version-n">Step {{ row.i + 1 }}<template v-if="row.s.name"> · {{ row.s.name }}</template></span>
              <span class="gtb-version-sub">{{ stepSnippet(row.s) }}</span>
            </div>
            <button type="button" class="gtb-mini" @click="insertCopyOf(row.i)" :title="'Add a copy of this step after Step ' + (insertPicker.after + 1)">⧉ Use a copy</button>
          </div>
        </div>
        <div class="gtb-versions-foot">The copy keeps the original's narration, highlight, blocks &amp; settings — tweak it after it's added.</div>
      </div>
    </div>
  </div>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
var feapi = new FeApiService();

function esc(s) { return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/[^a-zA-Z0-9_-]/g, "\\$&"); }
function selResolvesTo(sel, el) {
  try { return !!sel && document.querySelector(sel) === el; } catch (e) { return false; }
}
function nthOfType(el) {
  var n = 1, sib = el;
  while ((sib = sib.previousElementSibling)) { if (sib.tagName === el.tagName) n++; }
  return n;
}
// Classes that flip with STATE (selection, open/closed, value present). NEVER
// build a selector on these or the target vanishes when the state changes — this
// is exactly what stranded the machine-dropdown steps: Semantic UI toggles
// `.default` (placeholder) and rewrites the `.text` value, so a captured
// `div.default:nth-of-type(1)` only matched while NO machine was selected.
var DYN_CLASS = /(^|-)(active|open|shown?|selected|checked|hover|focus|focused|disabled|visible|transition|loading|animating|default|placeholder|empty|filled|is-|has-|ng-|v-|router-link)/;
function stableClasses(node) {
  if (!node.classList || !node.classList.length) return [];
  return Array.prototype.filter.call(node.classList, function (c) { return c && !DYN_CLASS.test(c); });
}
function stableClass(node) {
  var cls = stableClasses(node);
  return cls.length ? "." + esc(cls[0]) : "";
}
// A STABLE selector that already resolves to EXACTLY `el` — its id, a stable
// data/name/aria attribute, or a class that happens to be unique. Returns null if
// none (caller falls back to the positional nth-of-type path). Verified against
// the live DOM, so it can only be MORE durable than a positional path, never wrong.
var STABLE_ATTRS = ["data-part-code", "data-code", "data-id", "data-key", "data-test", "data-testid", "name", "aria-label"];
function stableSelectorFor(el) {
  if (!el || !el.tagName) return null;
  if (el.id) { var byId = "#" + esc(el.id); if (selResolvesTo(byId, el)) return byId; }
  for (var i = 0; i < STABLE_ATTRS.length; i++) {
    var v = el.getAttribute && el.getAttribute(STABLE_ATTRS[i]);
    if (v && String(v).length < 80) {
      var sa = el.tagName.toLowerCase() + "[" + STABLE_ATTRS[i] + '="' + String(v).replace(/(["\\])/g, "\\$1") + '"]';
      if (selResolvesTo(sa, el)) return sa;
    }
  }
  var cls = stableClasses(el);
  for (var j = 0; j < cls.length; j++) {
    var c1 = "." + esc(cls[j]); if (selResolvesTo(c1, el)) return c1;
    var c2 = el.tagName.toLowerCase() + c1; if (selResolvesTo(c2, el)) return c2;
  }
  if (cls.length > 1) { var all = el.tagName.toLowerCase() + "." + cls.map(esc).join("."); if (selResolvesTo(all, el)) return all; }
  return null;
}
// Build the MOST RELIABLE selector that resolves to EXACTLY `el`, in order of
// durability: (1) the element's own stable selector (id/data/name/unique class);
// (2) a tag(.stableClass):nth-of-type path ROOTED at the nearest ancestor that
// has a stable selector (id, data-part-code…) — so list reorder/filter can't
// shift which element position N hits; (3) the bare path. Each verified live.
function robustSelector(el) {
  if (!el || !el.tagName) return null;
  var own = stableSelectorFor(el);
  if (own) return own;
  var parts = [], node = el;
  for (var depth = 0; node && node.tagName && depth < 12; depth++) {
    if (node !== el) {
      var root = stableSelectorFor(node);
      if (root) {
        var rooted = parts.length ? (root + " " + parts.join(" > ")) : root;
        if (selResolvesTo(rooted, el)) return rooted;
      }
    }
    parts.unshift(node.tagName.toLowerCase() + stableClass(node) + ":nth-of-type(" + nthOfType(node) + ")");
    var candidate = parts.join(" > ");
    if (selResolvesTo(candidate, el)) return candidate;
    node = node.parentElement;
  }
  var full = parts.join(" > ");
  if (selResolvesTo(full, el)) return full;
  return el.id ? "#" + esc(el.id) : el.tagName.toLowerCase();
}
// For dropdown / select / combobox clicks, anchor the step to the CONTROL
// container (always present) instead of the value/placeholder/option node inside
// it (which appears and disappears with the selection). This is why a "select
// your machine" step must target #MachineSelect — not the .default/.text node
// that exists in only one state.
function meaningfulTarget(el) {
  if (!el || !el.closest) return el;
  var ctrl = el.closest('.ui.dropdown, .dropdown, select, [role="combobox"], [role="listbox"]');
  if (ctrl) return ctrl;
  return stableAnchor(el);
}
// A zoomable diagram's <img> / hotspot layer SCALES + PANS with the zoom, so a
// highlight anchored to it drifts (slides off into the blank area beside the
// drawing). Anchor instead to the stable wrapper panel, which doesn't move.
function stableAnchor(el) {
  if (!el || !el.closest) return el;
  var wrap = el.closest(".machine-image-wrap, .diagram-zoom-wrap, .callout-hotspot-layer");
  if (wrap) return wrap.closest(".machine-image-wrap") || wrap;
  if (el.tagName === "IMG") { var w = el.closest("figure, .ui.image, [class*='image-wrap']"); if (w) return w; }
  return el;
}
// Resolve a stored selector with RECOVERY: if the exact path no longer matches
// (e.g. an old dropdown-inner selector once the value changed), fall back to its
// leading stable token (#id / [attr]) — the control is still on the page. Then
// stableAnchor() so a diagram-image target snaps to its (non-scaling) wrapper.
// Heals already-saved steps without a re-pick.
function looseResolve(sel) {
  if (!sel) return null;
  var el = null;
  try { el = document.querySelector(sel); } catch (e) {}
  if (!el) {
    // Fall back to the leading STABLE token only (#id / [attr] — precise; never a
    // bare class, which could match the wrong element). Heals dropdown-inner
    // selectors → their control (e.g. "#MachineSelect > div.default…" → #MachineSelect).
    var m = /^\s*(#[\w-]+|\[[^\]]+\])/.exec(sel);
    if (m && m[1]) { try { el = document.querySelector(m[1]); } catch (e) {} }
  }
  return el ? stableAnchor(el) : null;
}
// Apply a saved box offset to an element's CURRENT rect, but GUARD it: if the
// offset would land the highlight far from the element (wrong element resolved,
// or a stale capture), discard the offset and just outline the element — a
// highlight must never fly off to a random spot. pad = outline padding.
function boxedRect(R, box, pad) {
  if (box) {
    var rect = { left: R.left + box.dx, top: R.top + box.dy, width: box.w, height: box.h };
    var nearX = (rect.left < R.right + 220) && (rect.left + rect.width > R.left - 220);
    var nearY = (rect.top < R.bottom + 220) && (rect.top + rect.height > R.top - 220);
    if (nearX && nearY && rect.width > 2 && rect.height > 2) return rect;
  }
  return { left: R.left - pad, top: R.top - pad, width: R.width + pad * 2, height: R.height + pad * 2 };
}
function labelFor(el) {
  var t = (el.textContent || "").trim().replace(/\s+/g, " ");
  if (t && t.length <= 32) return t;
  if (t) return t.slice(0, 30) + "…";
  return (el.getAttribute && (el.getAttribute("aria-label") || el.getAttribute("placeholder"))) || el.tagName.toLowerCase();
}
// A BROAD selector for masks/blocks: match EVERY element of the same kind (all
// cart buttons, all item boxes…) — never the one clicked instance or its value.
// Climbs to the nearest clickable/classed element and uses its most distinctive
// stable class. Returns null (caller falls back to robustSelector) if nothing
// classy. This is why a block applies to ALL the matching buttons in the list.
function broadSelector(el) {
  if (!el) return null;
  var node = (el.closest && el.closest('a,button,[role="button"],[class]')) || el;
  var cn = node.className;
  if (cn && cn.baseVal != null) cn = cn.baseVal;   // SVG className is an SVGAnimatedString
  var skip = /^(is-|has-|active|selected|disabled|open|show|visible|hidden|hover|focus|ng-|router-link|btn$|d-flex|d-none|m[trblxy]?-\d|p[trblxy]?-\d)/i;
  var list = String(cn || "").trim().split(/\s+/).filter(function (c) { return c && !skip.test(c); });
  return list.length ? "." + esc(list[0]) : null;
}
// Lightweight markup for the narration card: **bold**, _italic_, line breaks,
// and any raw HTML the admin types (admin-authored, so it's trusted). The
// SPOKEN narration uses plainText() so the voice never reads the tags/markers.
function renderMarkup(t) {
  t = String(t || "");
  t = t.replace(/\*\*([^*\n]+)\*\*/g, "<b>$1</b>");
  t = t.replace(/_([^_\n]+)_/g, "<i>$1</i>");
  t = t.replace(/\n/g, "<br>");
  return t;
}
function plainText(t) {
  return String(t || "")
    .replace(/\*\*/g, "").replace(/_/g, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&")
    .replace(/\s+/g, " ").trim();
}

export default {
  name: "GuideTourBuilder",
  data() {
    return {
      open: false,
      flowKey: "order",
      steps: [],
      pick: null, // { idx, kind }
      editing: null, // { idx, kind, selector, label, el, text, audio, box:{...} }
      recording: false,
      _recMr: null,
      _recChunks: [],
      _recStream: null,
      // Screen recording of a "Preview tour" run → uploaded as THE guide video for
      // this flow. Distinct from the per-step mic recorder above (_recMr).
      recordMode: "off",        // "off" | "audio" | "noaudio" — chosen in the start picker
      _screenMr: null,
      _screenChunks: [],
      _screenStream: null,
      spot: null,
      ringStyles: [],
      reflow: 0,             // bumped on scroll/resize so editDimRects recomputes against the new viewport
      // Panel "show on page" marks: clicking a highlight/block chip in the docked
      // panel outlines the matching element(s) on the LIVE page (no Preview/Resize).
      panelMarks: [],        // outline rects to draw
      panelMarkSel: "",      // the selector currently shown on the page (drives the outline)
      panelMarkKey: "",      // WHICH button owns that outline ("eye:<i>" / "hl:<i>:<hi>" / "mask:<i>:<mi>") — so two steps sharing one selector don't both light up
      panelMarkKind: "",     // 'highlight' (red) | 'mask' (green)
      // Highlight STYLE editor: clicking 🎨 on a highlight chip opens a box of style
      // controls (presets + border/fill color + thickness + line) with a LIVE on-page
      // preview. styleEdit = { i, hi } points at the highlight being styled.
      styleEdit: null,
      styleEditPos: null,    // {left, top} once the style box is dragged (else default bottom-left)
      stylePresets: [
        { name: "Red outline", s: { border: "#c0392b", bg: "", width: 2, line: "solid" } },
        { name: "Dashed", s: { border: "#c0392b", bg: "", width: 2, line: "dashed" } },
        { name: "Thick", s: { border: "#c0392b", bg: "", width: 4, line: "solid" } },
        { name: "Red fill", s: { border: "#c0392b", bg: "#c0392b", width: 2, line: "solid" } },
        { name: "Yellow marker", s: { border: "#e6b800", bg: "#ffeb3b", width: 0, line: "solid" } },
        { name: "Blue", s: { border: "#1976d2", bg: "", width: 3, line: "solid" } },
        { name: "Green", s: { border: "#2e7d32", bg: "", width: 3, line: "solid" } },
      ],
      maskStyles: [],        // edit-view overlays showing this step's click-blocks
      activeMaskSel: "",     // a block chip clicked → outline its element(s) in green
      note: "",
      noteKind: "",        // "" | "ok" | "err" — styles the confirmation banner
      savedInfo: null,     // after a recorded tour saves: { label, mb, format, path, url } — a PERSISTENT panel (the toast note auto-dismisses) so the admin can read/copy the save path
      pendingRec: null,    // a just-finished recording awaiting review: { label, flow, blob, url(objectURL), mb, fmt, saving } — drives the watch-then-save/discard modal
      ieExport: null,      // { sel: [bool per current step] } → the "pick which steps to export" modal
      ieImport: null,      // { rows: [{ step, map }], voice, flow, name } → the "what each incoming step replaces" import modal
      serverVersion: 0,    // latest version saved to the DB for this flow
      saving: false,
      showVersions: false,    // version-history overlay
      versionList: [],
      versionsLoading: false,
      copySource: "",     // flow selected in the "Copy from another tour" picker
      copyOptions: [],    // OTHER flows that have a saved tour (source choices)
      showStartPicker: false,   // "Preview tour — start at which step?" overlay
      insertPicker: { open: false, after: -1 },   // "＋ Insert a step" chooser: blank, or a copy of an existing step (inserted after index `after`)
      voice: "en-US-AriaNeural",   // Azure neural narration voice
      voiceOptions: [
        { id: "en-US-AriaNeural", label: "Aria (US, female)" },
        { id: "en-US-JennyNeural", label: "Jenny (US, female)" },
        { id: "en-US-AvaNeural", label: "Ava (US, female)" },
        { id: "en-US-EmmaNeural", label: "Emma (US, female)" },
        { id: "en-US-MichelleNeural", label: "Michelle (US, female)" },
        { id: "en-US-AndrewNeural", label: "Andrew (US, male)" },
        { id: "en-US-BrianNeural", label: "Brian (US, male)" },
        { id: "en-US-GuyNeural", label: "Guy (US, male)" },
      ],
      _actx: null,              // Web Audio context (resumed on first click)
      _bufCache: {},            // decoded AudioBuffers keyed by voice|text
      _curSrc: null,            // currently-playing BufferSource
      _onPick: null,
      _spotT: null,
      _noteT: null,
      _drag: null,
      _mm: null,
      _mu: null,
    };
  },
  computed: {
    flowLabel() {
      var known = { order: "New Order", quote: "New Quote", search: "New Search" };
      if (known[this.flowKey]) return known[this.flowKey];
      // Per-page flows are keyed by route name / slug — humanize for display:
      // "CreateOrder" → "Create Order", "account-orders" → "Account Orders".
      return String(this.flowKey || "")
        .replace(/[-_]+/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, function (c) { return c.toUpperCase(); })
        .trim() || this.flowKey;
    },
    // How many steps are checked in the export picker.
    ieExportCount() { return this.ieExport ? this.ieExport.sel.filter(Boolean).length : 0; },
    ieAllSelected() { return !!(this.ieExport && this.ieExport.sel.length && this.ieExport.sel.every(Boolean)); },
    // The wildcard tokens an admin can type, shown in the { } hover help on the
    // callout card. Add new tokens here as they're introduced.
    tokenHelpHtml() {
      return [
        "<b>Wildcard tokens</b>",
        "<span><code>{wait}</code> — pause the voice here until the customer changes the <b>first highlight</b> (e.g. the quantity) or clicks <b>Skip</b>, then speak the rest. A Skip/Next button shows automatically.</span>",
        "<span><code>{part}</code> / <code>{part code}</code> — a part's code. In regular narration it's the highlighted (or first-shown) part, same source as {part name}; in an <i>Input</i> step's <b>Result callout</b> it's the top matching part after the customer filters.</span>",
        "<span><code>{count}</code> — how many parts matched (Result callout).</span>",
        "<span><code>{machine name}</code> / <code>{assembly name}</code> — read back the customer's CURRENT machine / assembly pick in the narration (spoken + on-screen). The machine name stops at the first hyphen (e.g. “COMBINATION: 40F - DDF (610216)” → “COMBINATION: 40F”). Steps using these always re-synthesize so the voice says the live pick.</span>",
        "<span><code>{part name}</code> / <code>{part price}</code> — read back a PART's name / price. Uses the part this step highlights (point the 🎯 highlight at a part row), else the first part shown. Price matches the on-screen amount (e.g. “$229.50”).</span>",
        "<span><code>{showif:hasDrawing}</code>/<code>noDrawing</code> or <code>{showif:hasAssembly}</code>/<code>noAssembly</code> — show this step ONLY when the assembly has (or hasn't) a drawing, or the machine has (or hasn't) assemblies; the tour skips it otherwise. Easiest via the <b>Show:</b> dropdown on the step.</span>",
      ].join("");
    },
    // Screen recording needs getDisplayMedia + MediaRecorder (Chrome/Edge desktop).
    canRecord() {
      return !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia && window.MediaRecorder);
    },
    editStyle() {
      if (!this.editing || !this.editing.box) return {};
      var b = this.editing.box;
      return { left: b.left + "px", top: b.top + "px", width: b.width + "px", height: b.height + "px" };
    },
    // Panel "show on page" outlines render only while the panel is visible (not while
    // editing/picking) so they never linger over the edit view.
    shownPanelMarks() { return (this.editing || this.pick) ? [] : this.panelMarks; },
    // Once dragged, position the style box explicitly (overrides its default bottom-left).
    styleEditStyle() {
      return this.styleEditPos ? { left: this.styleEditPos.left + "px", top: this.styleEditPos.top + "px", bottom: "auto" } : {};
    },
    // The effective style of the highlight whose style box is open (for the controls).
    curStyle() {
      var se = this.styleEdit;
      var h = se && this.steps[se.i] && this.steps[se.i].highlights && this.steps[se.i].highlights[se.hi];
      return this.hlStyleOf(h);
    },
    // LIVE on-page preview of the highlight being styled: outline its element(s) with
    // the current style, updating as the controls change (and on scroll/resize).
    styleEditPreview() {
      void this.reflow;
      var se = this.styleEdit;
      if (!se || this.editing || this.pick) return [];
      var h = this.steps[se.i] && this.steps[se.i].highlights && this.steps[se.i].highlights[se.hi];
      if (!h || !h.selector) return [];
      var els = []; try { els = document.querySelectorAll(h.selector); } catch (e) { return []; }
      var out = [];
      for (var k = 0; k < els.length; k++) {
        var R = els[k].getBoundingClientRect();
        if (R.width < 2 || R.height < 2) continue;
        out.push(this.ringStyleFor(h, boxedRect(R, h.box, 4)));
      }
      return out;
    },
    // Multi-hole page dim for the edit view: the viewport MINUS every highlight (the
    // adjustable box + every extra ring) → strips covering the rest, so EVERY
    // highlight punches through (ported from the player's guardRects). [] when there's
    // no adjustable box (target not on the page yet → nothing to dim around).
    editDimRects() {
      void this.reflow;   // recompute on scroll/resize (reposition bumps it)
      if (!this.editing || !this.editing.box) return [];
      var vw = window.innerWidth, vh = window.innerHeight;
      var holes = [];
      var b = this.editing.box;
      if (b.width > 1 && b.height > 1) holes.push({ l: Math.floor(b.left), t: Math.floor(b.top), r: Math.ceil(b.left + b.width), bt: Math.ceil(b.top + b.height) });
      (this.ringStyles || []).forEach(function (rg) {
        var s = (rg && rg.style) || rg || {};
        var l = parseFloat(s.left), t = parseFloat(s.top), w = parseFloat(s.width), h = parseFloat(s.height);
        if (w > 1 && h > 1) holes.push({ l: Math.floor(l), t: Math.floor(t), r: Math.ceil(l + w), bt: Math.ceil(t + h) });
      });
      if (!holes.length) return [];
      var free = [{ l: 0, t: 0, r: vw, bt: vh }];
      holes.forEach(function (hole) {
        var next = [];
        free.forEach(function (f) {
          if (hole.r <= f.l || hole.l >= f.r || hole.bt <= f.t || hole.t >= f.bt) { next.push(f); return; }   // no overlap
          var hl = Math.max(f.l, hole.l), hr = Math.min(f.r, hole.r), ht = Math.max(f.t, hole.t), hb = Math.min(f.bt, hole.bt);
          if (f.t < ht) next.push({ l: f.l, t: f.t, r: f.r, bt: ht });   // band above
          if (hb < f.bt) next.push({ l: f.l, t: hb, r: f.r, bt: f.bt });  // band below
          if (f.l < hl) next.push({ l: f.l, t: ht, r: hl, bt: hb });      // strip left
          if (hr < f.r) next.push({ l: hr, t: ht, r: f.r, bt: hb });      // strip right
        });
        free = next;
      });
      return free.map(function (f) { return { left: f.l + "px", top: f.t + "px", width: Math.max(0, f.r - f.l) + "px", height: Math.max(0, f.bt - f.t) + "px" }; });
    },
    editCardStyle() {
      if (!this.editing) return {};
      // Once the admin drags the card, honor that explicit position.
      if (this.editing.cardLeft != null) return { left: this.editing.cardLeft + "px", top: this.editing.cardTop + "px" };
      // Dock to the RIGHT edge by default so the card never covers the
      // highlighted control or the dropdown menu it opens (page content + its
      // menus sit center-left). Draggable from here via the ✥ Step header.
      var vw = window.innerWidth, cardW = 340;
      return { left: Math.max(12, vw - cardW - 20) + "px", top: "84px" };
    },
    hasContent() {
      return this.steps.some(function (s) { return (s.text && s.text.trim()) || s.target; });
    },
    // The steps that will actually be persisted (drops empty placeholder steps).
    savableSteps() {
      return this.steps.filter(function (s) { return (s.text && s.text.trim()) || s.target || s.audio; });
    },
    // Steps offered as copy sources in the ＋ insert chooser — those with content,
    // each paired with its ORIGINAL index (so the label + the copy use that index).
    reusableSteps() {
      return this.steps
        .map(function (s, i) { return { s: s, i: i }; })
        .filter(function (r) { return !!(r.s && ((r.s.text && r.s.text.trim()) || r.s.target || (r.s.highlights && r.s.highlights.length))); });
    },
    voiceLabel() {
      var self = this;
      var v = this.voiceOptions.filter(function (x) { return x.id === self.voice; })[0];
      return v ? v.label.replace(/\s*\(.*\)$/, "") : self.voice;
    },
  },
  watch: {
    // Continuously cache the draft so it survives the page reload that nav
    // causes — the builder reloads these steps when it resumes on the next page.
    steps: { deep: true, handler: function () { this.persistDraft(); } },
    // While the builder session is open, push the page out from under the docked
    // panel so the page's OWN controls (e.g. the "Show wear parts only" toggle)
    // stay visible/clickable. Keyed on `open` (not panel-visible) so the layout
    // stays stable through pick/edit too — and previewAll sets open=false first,
    // so the live tour's viewport-fixed spotlight is never pushed out of align.
    open: { immediate: true, handler: function (v) { try { document.body.classList.toggle("gtb-pushed", !!v); } catch (e) {} } },
  },
  mounted() {
    this._openEvt = this.openBuilder.bind(this);
    this.$root.$on("open-tour-builder", this._openEvt);
    // prime SpeechSynthesis voices
    if (window.speechSynthesis) { try { window.speechSynthesis.getVoices(); } catch (e) {} }
    // Keep the highlight box aligned to its element on resize/scroll.
    this._onReflow = this.reposition.bind(this);
    window.addEventListener("resize", this._onReflow);
    window.addEventListener("scroll", this._onReflow, true);
    // Resume an in-progress build session so the builder "follows" the admin to
    // whatever page they navigate to (it survives the nav page-reload). The ✕
    // Close button ends the session.
    try {
      var sess = localStorage.getItem("jcs_tour_build_session");
      var liveTour = localStorage.getItem("jcs_tour_active");   // a live tour playback is resuming → stand down, don't pop the builder over it
      var ra = (this.$store && this.$store.getters) ? this.$store.getters.isRealAdmin : undefined;
      var allow = (ra === undefined) ? true : !!ra;   // resume only for real admins
      if (sess && allow && !liveTour && !this.open) { var self = this; this.$nextTick(function () { self.openBuilder(sess, true); }); }
    } catch (e) {}
  },
  beforeDestroy() {
    this.$root.$off("open-tour-builder", this._openEvt);
    if (this._onReflow) {
      window.removeEventListener("resize", this._onReflow);
      window.removeEventListener("scroll", this._onReflow, true);
    }
    this.cancelPick();
    this.stopSpeak();
    this.stopRecord();
    if (this._screenMr) this._stopScreenRecording();
    this._clearEditWatch();
    this._revokePendingRec();
    try { document.body.classList.remove("gtb-pushed"); } catch (e) {}
  },
  methods: {
    openBuilder(flowKey, resume) {
      // If a "Preview tour" was being recorded, the tour just ended (the widget
      // re-opens us). Stop the recorder → it asks to save + uploads as the guide video.
      if (this._screenMr) {
        this._stopScreenRecording();
      } else {
        // Recording was requested but the live recorder is gone → a page reload
        // (multi-page tour nav) tore it down. Tell the admin instead of failing silently.
        var wanted = false;
        try { wanted = !!localStorage.getItem("jcs_tour_screen_rec"); } catch (e) {}
        if (wanted) {
          try { localStorage.removeItem("jcs_tour_screen_rec"); } catch (e) {}
          // The player stamps the step/URL on beforeunload — surface exactly WHERE the
          // hard reload happened so the admin can fix that step (instead of a blind reject).
          var brk = null;
          try { brk = JSON.parse(localStorage.getItem("jcs_tour_rec_break") || "null"); localStorage.removeItem("jcs_tour_rec_break"); } catch (e) {}
          var where = brk
            ? " It broke at step " + brk.step + (brk.total ? " of " + brk.total : "") + (brk.url ? " — that step reloaded the page to “" + brk.url + "”." : ".")
            : "";
          var self0 = this;
          this.$nextTick(function () {
            self0.showNote("The recording stopped because a step did a full PAGE RELOAD (the in-tab recorder can't survive that)." + where + " Fix that step so it navigates in-app, or record a multi-page walkthrough with a desktop screen recorder.", "err");
          });
        }
      }
      this.flowKey = flowKey || "order";
      // A build SESSION makes the builder follow the admin across pages (it
      // re-opens on every portal page until they ✕ Close). If one is already
      // active we're mid-build, so keep the local draft rather than pulling the
      // saved server copy over unsaved cross-page work. See mounted().
      var hadSession = false;
      try { hadSession = (localStorage.getItem("jcs_tour_build_session") === this.flowKey); } catch (e) {}
      try { localStorage.setItem("jcs_tour_build_session", this.flowKey); } catch (e) {}
      try { var v = localStorage.getItem("jcs_tour_voice"); if (v) this.voice = v; } catch (e) {}
      // Open instantly with the local cache (or a blank step)…
      this.loadIntoSteps(this.readLocal());
      this.open = true;
      this.loadCopyOptions();   // populate the "copy from another tour" picker
      this.focusBuilderStep();  // if a Preview tour just ended, scroll back to the step it was on
      var keepLocal = !!resume || hadSession;
      var self = this;
      // Populate the voice dropdown with the voices THIS Azure resource actually
      // offers (so unavailable ones — which synthesize silently — aren't pickable).
      // Falls back to the hard-coded list if the endpoint isn't there yet.
      feapi.getTtsVoices().then(function (list) {
        if (list && list.length) {
          self.voiceOptions = list;
          if (!list.some(function (o) { return o.id === self.voice; })) {
            self.voice = list[0].id;
            try { localStorage.setItem("jcs_tour_voice", self.voice); } catch (e) {}
          }
        }
        self.prefetchAll();   // warm clips for the (now-confirmed) voice
      }).catch(function () { self.prefetchAll(); });
      feapi.getGuideTours().then(function (all) {
        var rec = all && all[self.flowKey];
        if (rec && rec.version) self.serverVersion = rec.version;
        // Only a brand-new open (no active session) pulls the saved tour over the draft.
        if (!keepLocal && rec && rec.steps && rec.steps.some(function (s) { return s && s.text; })) {
          self.loadIntoSteps(rec);
          self.prefetchAll();
        }
      }).catch(function () { /* backend not reachable yet → keep local */ });
      // "New Video" entry (catalog) → jump straight to the record-mode picker once
      // this flow's steps are present.
      this._maybeAutoOpenRecord();
    },
    // If the catalog's "New Video" button opened us (localStorage flag = this flow),
    // auto-open the Preview-tour record picker (audio / no-audio) as soon as steps
    // are loaded — steps may arrive async from the server, so poll briefly. Gives up
    // quietly if the tour has no steps yet (the admin builds them, then records).
    _maybeAutoOpenRecord() {
      var self = this;
      var want = false;
      try { want = localStorage.getItem("jcs_tour_autorecord") === this.flowKey; } catch (e) {}
      if (!want) return;
      try { localStorage.removeItem("jcs_tour_autorecord"); } catch (e) {}
      var tries = 0;
      var tick = function () {
        if (!self.open) return;                  // builder closed → abort
        if (self.showStartPicker) return;        // already open
        if (self.hasContent) { self.openStartPicker(); return; }
        if (++tries > 40) return;                // ~10s — empty tour, just leave the builder open
        setTimeout(tick, 250);
      };
      setTimeout(tick, 300);
    },
    readLocal() {
      try { return JSON.parse(localStorage.getItem("jcs_tour_rec_" + this.flowKey) || "null"); } catch (e) { return null; }
    },
    // Populate the editable steps from a saved tour, else one blank step. Only
    // resumes real BUILDER data (steps with typed text); ignores leftover
    // old mic-recorder data (target/highlights, no text).
    loadIntoSteps(existing) {
      var self = this;
      var hasBuilderData = existing && existing.steps && existing.steps.some(function (s) { return s && (s.text || s.target || s.audio); });
      if (hasBuilderData) {
        this.steps = existing.steps.map(function (s) { return self._normalizeStep(s); });
      } else {
        this.steps = [this.blankStep()];
      }
    },
    // Map a raw (exported / stored) step into the builder's working step shape.
    _normalizeStep(s) {
      s = s || {};
      return {
        text: s.text || "",
        audio: s.audio || null,
        target: s.target || null,
        targetLabel: s.targetLabel || (s.target || ""),
        name: s.name || "",
        targetBox: s.targetBox || null,
        type: s.type || null,
        showNext: !!s.showNext,
        resultMsg: s.resultMsg || "",
        searchCtx: s.searchCtx || null,
        masks: (s.masks || []).map(function (m) {
          return typeof m === "string" ? { selector: m, label: m } : { selector: m.selector, label: m.label || "" };
        }),
        highlights: (s.highlights || []).map(function (h) {
          return typeof h === "string"
            ? { selector: h, label: h, box: null, hlStyle: null }
            : { selector: h.selector, label: h.label, box: h.box || null, hlStyle: h.hlStyle || null };
        }),
      };
    },
    blankStep() { return { text: "", audio: null, target: null, targetLabel: "", name: "", highlights: [], masks: [], type: null, showNext: false, resultMsg: "", searchCtx: null }; },
    setDropdown(s, on) { this.$set(s, "type", on ? "dropdown" : null); },
    setInput(s, on) { this.$set(s, "type", on ? "input" : null); },
    setCart(s, on) { this.$set(s, "type", on ? "cart" : null); },
    setShowNext(s, on) { this.$set(s, "showNext", !!on); },
    // Conditional visibility, stored as a leading {showif:…} marker in the step text
    // (round-trips with NO schema change; the backend strips it from the voice and the
    // player strips it from the caption + skips the step when the page state doesn't match).
    stepShowIf(s) {
      var m = String((s && s.text) || "").match(/\{showif:\s*([a-z]+)\s*\}/i);
      return m ? m[1].toLowerCase() : "";
    },
    setStepShowIf(s, val) {
      if (!s) return;
      var t = String(s.text || "").replace(/\{showif:\s*[a-z]+\s*\}\s*/gi, "");   // drop any existing marker
      if (val) t = "{showif:" + val + "} " + t;                                     // prepend the chosen one
      this.$set(s, "text", t);
    },
    removeMask(i, mi) { var s = this.steps[i]; if (s && s.masks) s.masks.splice(mi, 1); },
    removeHighlight(i, hi) { var s = this.steps[i]; if (s && s.highlights) s.highlights.splice(hi, 1); },
    // ===== Highlight STYLE editor =====
    // Effective style for a highlight (defaults match the classic red outline).
    hlStyleOf(h) {
      var s = (h && h.hlStyle) || {};
      return {
        border: s.border || "#c0392b",
        bg: s.bg || "",
        width: (s.width != null ? s.width : 2),
        line: s.line || "solid",
        anim: s.anim || "none",   // none | glow | sonar | pulse | blink | bounce | shake
        animCount: s.animCount || "infinite",   // how many times the animation runs ("infinite" = all the time)
      };
    },
    // hex (#rrggbb) → rgba string at alpha a (so a fill is translucent, not opaque).
    _rgba(hex, a) {
      var m = /^#?([0-9a-fA-F]{6})$/.exec(hex || "");
      if (!m) return hex || "transparent";
      var n = parseInt(m[1], 16);
      return "rgba(" + ((n >> 16) & 255) + "," + ((n >> 8) & 255) + "," + (n & 255) + "," + a + ")";
    },
    // The CSS for a highlight's ring/box from its style (border color/width/line + a
    // translucent fill). Shared by the rings, the live preview, and (mirrored) the player.
    ringStyleFor(h, rect) {
      var s = this.hlStyleOf(h);
      return {
        left: rect.left + "px", top: rect.top + "px", width: rect.width + "px", height: rect.height + "px",
        borderColor: s.border || "transparent",
        borderWidth: (s.width || 0) + "px",
        borderStyle: s.width > 0 ? s.line : "none",
        backgroundColor: s.bg ? this._rgba(s.bg, 0.3) : "transparent",
        color: s.border || "#c0392b",   // drives currentColor for the glow/sonar box-shadows
        anim: s.anim,                    // ignored as CSS by :style; read for the animation class
        animationIterationCount: String(s.animCount),   // overrides the class's "infinite" with the chosen count
      };
    },
    // Open the style box for a highlight (scrolls its element into view so the live
    // preview is visible).
    openStyleEdit(i, hi) {
      var h = this.steps[i] && this.steps[i].highlights && this.steps[i].highlights[hi];
      if (!h) return;
      if (!h.hlStyle) this.$set(h, "hlStyle", this.hlStyleOf(h));
      this.styleEdit = { i: i, hi: hi };
      try { var el = document.querySelector(h.selector); if (el) el.scrollIntoView({ behavior: "auto", block: "center" }); } catch (e) {}
    },
    closeStyleEdit() { this.styleEdit = null; },
    // Drag the style box by its header.
    startStyleMove(e) {
      var box = e.currentTarget && e.currentTarget.parentElement;
      var r = box ? box.getBoundingClientRect() : { left: 16, top: window.innerHeight - 360 };
      this.styleEditPos = { left: Math.round(r.left), top: Math.round(r.top) };   // pin to current spot before dragging
      this._sdrag = { x: e.clientX, y: e.clientY, l: this.styleEditPos.left, t: this.styleEditPos.top };
      this._smm = this.onStyleMove.bind(this);
      this._smu = this.endStyleMove.bind(this);
      document.addEventListener("mousemove", this._smm, true);
      document.addEventListener("mouseup", this._smu, true);
    },
    onStyleMove(e) {
      if (!this._sdrag) return;
      var vw = window.innerWidth, vh = window.innerHeight;
      var l = this._sdrag.l + (e.clientX - this._sdrag.x);
      var t = this._sdrag.t + (e.clientY - this._sdrag.y);
      this.styleEditPos = { left: Math.min(Math.max(l, 4), vw - 80), top: Math.min(Math.max(t, 4), vh - 40) };
    },
    endStyleMove() {
      if (this._smm) { document.removeEventListener("mousemove", this._smm, true); this._smm = null; }
      if (this._smu) { document.removeEventListener("mouseup", this._smu, true); this._smu = null; }
      this._sdrag = null;
    },
    setStyle(key, val) {
      var se = this.styleEdit; if (!se) return;
      var h = this.steps[se.i] && this.steps[se.i].highlights && this.steps[se.i].highlights[se.hi]; if (!h) return;
      if (!h.hlStyle) this.$set(h, "hlStyle", this.hlStyleOf(h));
      this.$set(h.hlStyle, key, val);
      this.refreshEditRings();   // live update if the edit view is also open
    },
    applyStylePreset(p) {
      var se = this.styleEdit; if (!se || !p) return;
      var h = this.steps[se.i] && this.steps[se.i].highlights && this.steps[se.i].highlights[se.hi]; if (!h) return;
      this.$set(h, "hlStyle", Object.assign({}, p.s));
      this.refreshEditRings();
    },
    // Remove the step's primary 🎯 highlight (clears target/label/box). The step keeps
    // its narration + any extra highlights/blocks; the 🎯 button reverts to "Pick…".
    clearTarget(i) {
      var s = this.steps[i];
      if (!s) return;
      if (this.panelMarkKey === "eye:" + i) this._clearPanelMarks();
      this.$set(s, "target", null);
      this.$set(s, "targetLabel", "");
      this.$set(s, "targetBox", null);
    },
    // The ✕ on the on-page red box: force-delete whichever highlight is being
    // edited — an EXTRA highlight (areaIndex≥0) is removed from the step's list; the
    // PRIMARY target (areaIndex<0) is cleared. The box hides; the edit card stays
    // open so the admin can re-pick a target or add a highlight.
    deleteEditingHighlight() {
      if (!this.editing) return;
      var idx = this.editing.idx, ai = this.editing.areaIndex, s = this.steps[idx];
      if (ai >= 0) {
        if (s && s.highlights && ai < s.highlights.length) s.highlights.splice(ai, 1);
      } else {
        this.clearTarget(idx);
      }
      this.$set(this.editing, "box", null);
      this.$set(this.editing, "areaIndex", -1);
      this.refreshEditRings();
    },
    addStep() { this.steps.push(this.blankStep()); },
    insertAfter(i) { this.steps.splice(i + 1, 0, this.blankStep()); },   // add a new step right after step i
    removeStep(i) { this.steps.splice(i, 1); if (!this.steps.length) this.steps.push(this.blankStep()); },
    // ＋ on a step → ask whether to insert a blank step or REUSE a copy of an
    // existing one. The chosen step is dropped in right after step `i`.
    openInsertPicker(i) {
      this.showVersions = false; this.showStartPicker = false;   // don't stack overlays
      this.insertPicker = { open: true, after: i };
    },
    insertBlankAt() {
      var i = this.insertPicker.after;
      this.insertAfter(i);
      this.insertPicker.open = false;
      this.flashStep(i + 1);
    },
    // Drop a deep COPY of step `srcIndex` right after the step the ＋ was on. The
    // clone is independent (own narration/highlight/box/masks/settings), so editing
    // it never touches the original.
    insertCopyOf(srcIndex) {
      var src = this.steps[srcIndex];
      if (!src) { this.insertPicker.open = false; return; }
      var i = this.insertPicker.after;
      this.steps.splice(i + 1, 0, this.cloneStep(src));
      this.insertPicker.open = false;
      this.flashStep(i + 1);
    },
    // Structural clone of a step. Steps are plain data (strings/numbers/arrays of
    // {selector,box} — no functions/DOM refs), so a JSON round-trip copies every
    // own property faithfully, including targetBox and per-highlight boxes.
    cloneStep(s) {
      try { return JSON.parse(JSON.stringify(s)); } catch (e) { return this.blankStep(); }
    },
    // Scroll the just-inserted step into view and flash it (reuses the same focus
    // animation as returning from a Preview tour).
    flashStep(index) {
      var self = this;
      this.$nextTick(function () {
        try {
          var els = self.$el && self.$el.querySelectorAll ? self.$el.querySelectorAll(".gtb-steps .gtb-step") : null;
          var el = els && els[index];
          if (!el) return;
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("gtb-step-focus");
          setTimeout(function () { try { el.classList.remove("gtb-step-focus"); } catch (e) {} }, 2200);
        } catch (e) {}
      });
    },
    move(i, d) {
      var j = i + d;
      if (j < 0 || j >= this.steps.length) return;
      var s = this.steps.splice(i, 1)[0];
      this.steps.splice(j, 0, s);
    },

    // ---- pick an element on the page ----
    startPick(idx, kind) {
      this.cancelPick();
      this._clearPanelMarks();   // drop any panel "show on page" outline before picking
      this.pick = { idx: idx, kind: kind };
      var self = this;
      this._onPick = function (e) {
        var raw = e.target;
        if (raw && raw.closest && (raw.closest(".gtb-panel") || raw.closest(".gtb-pickbar") || raw.closest(".gtb-edit") || raw.closest(".gtb-edit-card"))) return;
        e.preventDefault();
        e.stopPropagation();
        var idx = self.pick.idx, kind = self.pick.kind;
        self.cancelPick();
        // Target/highlight: anchor dropdown/select clicks to the stable CONTROL
        // (e.g. #MachineSelect), not the value/placeholder node inside it — that's
        // what made "select your machine" steps vanish in the wrong state. Masks
        // stay on the raw element (broadSelector climbs to the right level).
        var t = kind === "mask" ? raw : meaningfulTarget(raw);
        var sel = robustSelector(t);
        if (!sel) return;
        if (kind === "mask") {
          // Masks are BROAD on purpose: block EVERY element of this kind (all
          // cart buttons, all item boxes…), not the one clicked instance — so a
          // block on one cart blocks them all. Falls back to the narrow selector
          // only if the element has no usable class.
          var bsel = broadSelector(t) || sel;
          var n = 0; try { n = document.querySelectorAll(bsel).length; } catch (e) {}
          var mstep = self.steps[idx];
          if (mstep) {
            if (!mstep.masks) self.$set(mstep, "masks", []);
            if (!mstep.masks.some(function (m) { return m.selector === bsel; })) {
              mstep.masks.push({ selector: bsel, label: bsel + (n > 1 ? " (×" + n + ")" : "") });
            }
          }
          self.flashEl(t);   // brief confirm flash on the picked element
          self.refreshEditMasks();   // draw the new block as a faint overlay now
          return;
        }
        if (kind === "highlight") {
          // An EXTRA highlight on this step, precise to the clicked element. Open it
          // AS the adjustable box right away so the admin can move/resize it (it was
          // only a static ring before). The 🎯 target stays the primary highlight.
          var hstep = self.steps[idx];
          if (hstep) {
            if (!hstep.highlights) self.$set(hstep, "highlights", []);
            var hi = -1;
            hstep.highlights.forEach(function (h, j) { if (h.selector === sel) hi = j; });
            if (hi < 0) hi = hstep.highlights.push({ selector: sel, label: labelFor(t), box: null }) - 1;
            self.flashEl(t);
            self.cancelPick();          // end the pick
            self.commitEditBox();       // keep the box currently being edited
            self.editExisting(idx, "highlight", sel, hstep.highlights[hi].label, hstep.highlights[hi].box, hi);
          } else {
            self.refreshEditRings();
          }
          return;
        }
        // Open the ADJUST box over the clicked element — it stays until OK.
        self.beginEdit(idx, kind, sel, labelFor(t), t);
      };
      document.addEventListener("click", this._onPick, true);
      this._escPick = function (e) { if (e.key === "Escape") self.cancelPick(); };
      document.addEventListener("keydown", this._escPick, true);
    },
    cancelPick() {
      if (this._onPick) { document.removeEventListener("click", this._onPick, true); this._onPick = null; }
      if (this._escPick) { document.removeEventListener("keydown", this._escPick, true); this._escPick = null; }
      this.pick = null;
    },

    // ---- adjustable highlight box (move / resize, commit on OK) ----
    beginEdit(idx, kind, selector, label, el) {
      this._clearEditWatch();
      try { el.scrollIntoView({ behavior: "auto", block: "center" }); } catch (e) {}   // instant: read rect at final position
      var r = el.getBoundingClientRect();
      this.editing = {
        idx: idx, kind: kind, selector: selector, label: label, el: el, areaIndex: -1,
        text: (this.steps[idx] && this.steps[idx].text) || "",
        audio: (this.steps[idx] && this.steps[idx].audio) || null,
        box: { left: Math.round(r.left), top: Math.round(r.top), width: Math.round(r.width), height: Math.round(r.height) },
        elRect: { left: r.left, top: r.top },   // element's last-known position (for resize realignment)
        cardLeft: null, cardTop: null,
      };
      // Snapshot the Search-page selections this target was picked on (assembly /
      // sub-assembly / filter / wear) so "Preview / resize" can later restore the
      // screen and the target is present. The Search page writes the context to
      // localStorage synchronously when we emit, so we can read it right after.
      try {
        this.$root.$emit("tour:capture-search-ctx");
        var sctx = JSON.parse(localStorage.getItem("jcs_tour_search_ctx") || "null");
        if (sctx && this.steps[idx]) this.$set(this.steps[idx], "searchCtx", sctx);
      } catch (e) {}
    },
    startMove(e) { this._beginDrag("move", e); },
    startResize(e) { this._beginDrag("resize", e); },
    // Drag the narration card itself (by its header) so it never blocks the page.
    startCardMove(e) {
      if (!this.editing) return;
      if (this.editing.cardLeft == null) {
        var cs = this.editCardStyle;
        this.editing.cardLeft = parseInt(cs.left, 10) || 12;
        this.editing.cardTop = parseInt(cs.top, 10) || 12;
      }
      this._drag = { mode: "card", x: e.clientX, y: e.clientY, cl: this.editing.cardLeft, ct: this.editing.cardTop };
      this._mm = this.onDragMove.bind(this);
      this._mu = this.endDrag.bind(this);
      document.addEventListener("mousemove", this._mm, true);
      document.addEventListener("mouseup", this._mu, true);
    },
    _beginDrag(mode, e) {
      if (!this.editing) return;
      this._drag = { mode: mode, x: e.clientX, y: e.clientY, box: Object.assign({}, this.editing.box) };
      this._mm = this.onDragMove.bind(this);
      this._mu = this.endDrag.bind(this);
      document.addEventListener("mousemove", this._mm, true);
      document.addEventListener("mouseup", this._mu, true);
    },
    onDragMove(e) {
      if (!this._drag || !this.editing) return;
      var dx = e.clientX - this._drag.x, dy = e.clientY - this._drag.y, b = this._drag.box;
      if (this._drag.mode === "move") {
        this.editing.box.left = b.left + dx;
        this.editing.box.top = b.top + dy;
      } else if (this._drag.mode === "card") {
        this.editing.cardLeft = this._drag.cl + dx;
        this.editing.cardTop = this._drag.ct + dy;
      } else {
        this.editing.box.width = Math.max(24, b.width + dx);
        this.editing.box.height = Math.max(18, b.height + dy);
      }
    },
    endDrag() {
      if (this._mm) { document.removeEventListener("mousemove", this._mm, true); this._mm = null; }
      if (this._mu) { document.removeEventListener("mouseup", this._mu, true); this._mu = null; }
      this._drag = null;
    },
    // Save the box CURRENTLY being adjusted (+ the narration typed so far) back to its
    // target/highlight WITHOUT closing the editor — used when switching the adjustable
    // box to another highlight (click a ring / add a new one) so nothing is lost.
    commitEditBox() {
      var ed = this.editing;
      if (!ed) return;
      var step = this.steps[ed.idx];
      if (step) { step.text = ed.text || ""; step.audio = ed.audio || null; }   // keep in-progress narration across the switch
      if (!ed.el || !ed.box || !step) return;
      var R = ed.el.getBoundingClientRect();
      var box = { dx: Math.round(ed.box.left - R.left), dy: Math.round(ed.box.top - R.top), w: Math.round(ed.box.width), h: Math.round(ed.box.height) };
      if (ed.kind === "target") {
        step.target = ed.selector; step.targetLabel = ed.label; step.targetBox = box;
      } else if (ed.areaIndex >= 0 && step.highlights && step.highlights[ed.areaIndex]) {
        step.highlights[ed.areaIndex].box = box;
      } else if (step.highlights) {
        var ex = step.highlights.filter(function (h) { return h.selector === ed.selector; })[0];
        if (ex) ex.box = box; else step.highlights.push({ selector: ed.selector, label: ed.label, box: box });
      }
    },
    // Click a ring → make THAT highlight (an extra by index, or the primary "target")
    // the adjustable box so it can be moved/resized. Commits the box being edited first.
    editRing(hi) {
      if (!this.editing || hi == null || this._drag) return;
      if (hi === this.editing.areaIndex) return;                                   // already editing this extra
      if (hi === "primary" && this.editing.kind === "target") return;              // already editing the primary
      var idx = this.editing.idx, step = this.steps[idx];
      if (!step) return;
      this.commitEditBox();
      if (hi === "primary") {
        this.editExisting(idx, "target", step.target, step.targetLabel || step.target, step.targetBox, -1);
      } else if (step.highlights && step.highlights[hi]) {
        var h = step.highlights[hi];
        this.editExisting(idx, "highlight", h.selector, h.label, h.box, hi);
      }
    },
    // Panel chip click → OUTLINE the matching element(s) on the live page (no need to
    // open Preview/Resize). Toggles off when the same chip is clicked again; scrolls
    // the first match into view so it's findable.
    markOnPage(kind, selector, key) {
      if (!selector) return;
      key = key || selector;
      // Toggle on the BUTTON identity, not the selector — clicking a different step that
      // happens to target the same element moves the outline there instead of toggling off.
      if (this.panelMarkKey === key) { this._clearPanelMarks(); return; }   // toggle off (same button)
      var els = []; try { els = document.querySelectorAll(selector); } catch (e) {}
      if (!els.length) {
        this._clearPanelMarks();   // don't leave the chip stuck "active" with nothing shown
        this.showNote("That element isn't on the page right now — navigate the page (select the machine/assembly) so it appears, then click again.", "info");
        return;
      }
      this.panelMarkSel = selector;
      this.panelMarkKey = key;
      this.panelMarkKind = kind;
      try { els[0].scrollIntoView({ behavior: "auto", block: "center" }); } catch (e) {}
      this._refreshPanelMarks();
    },
    _refreshPanelMarks() {
      if (!this.panelMarkSel) { this.panelMarks = []; return; }
      var els = []; try { els = document.querySelectorAll(this.panelMarkSel); } catch (e) { this.panelMarks = []; return; }
      var out = [];
      for (var i = 0; i < els.length; i++) {
        var R = els[i].getBoundingClientRect();
        if (R.width < 2 || R.height < 2) continue;
        out.push({ left: (R.left - 3) + "px", top: (R.top - 3) + "px", width: (R.width + 6) + "px", height: (R.height + 6) + "px" });
      }
      this.panelMarks = out;
    },
    _clearPanelMarks() { this.panelMarkSel = ""; this.panelMarkKey = ""; this.panelMarkKind = ""; this.panelMarks = []; },
    // How many elements a highlight's selector matches on the page RIGHT NOW. The
    // preview/player draw a box on EACH match, so anything other than 1 is worth
    // flagging: 0 = not on this page, >1 = a broad selector painting several boxes
    // (the usual "why are there extra/overlapping boxes?" cause). Reads `reflow` so
    // it recomputes on scroll/resize. Uses the same querySelectorAll as markOnPage.
    hlMatchCount(selector) {
      void this.reflow;
      if (!selector) return 0;
      try { return document.querySelectorAll(selector).length; } catch (e) { return 0; }
    },
    // True if any of a step's secondary highlights resolve to ≠1 elements — surfaced as
    // a ⚠ on the collapsed summary so a broad/missing selector is still caught at a
    // glance now that the per-highlight chips (and their ×N badges) live in Preview/Resize.
    stepHlWarn(s) {
      void this.reflow;
      var hs = (s && s.highlights) || [];
      for (var k = 0; k < hs.length; k++) { if (this.hlMatchCount(hs[k].selector) !== 1) return true; }
      return false;
    },
    // Keep the edit box aligned to its element when the page resizes/scrolls:
    // shift the box by however much the element moved (preserves any drag offset).
    reposition() {
      // Panel "show on page" outlines track scroll/resize while the panel is shown.
      if (this.panelMarkSel && !this.editing && !this.pick) this._refreshPanelMarks();
      if (!this.editing || this._drag) return;
      // keep the primary adjustable box glued to its element
      if (this.editing.el && this.editing.box) {
        try {
          var R = this.editing.el.getBoundingClientRect();
          if (!(R.width < 1 && R.height < 1)) {
            var er = this.editing.elRect || { left: R.left, top: R.top };
            this.editing.box.left = Math.round(this.editing.box.left + (R.left - er.left));
            this.editing.box.top = Math.round(this.editing.box.top + (R.top - er.top));
            this.editing.elRect = { left: R.left, top: R.top };
          }
        } catch (e) {}
      }
      // keep the step's EXTRA highlights + block overlays drawn live
      this.refreshEditRings();
      this.refreshEditMasks();
      this.reflow++;   // viewport may have changed → recompute the multi-hole dim
    },
    // Draw the step's click-BLOCKS in the edit view: every matched element gets a
    // faint overlay (so the admin sees what's blocked), and the block whose chip is
    // clicked (activeMaskSel) gets a bold GREEN outline so it's identifiable at a
    // glance. Cleared when the card closes.
    refreshEditMasks() {
      if (!this.editing) { this.maskStyles = []; return; }
      var self = this, step = this.steps[this.editing.idx];
      var out = [];
      ((step && step.masks) || []).forEach(function (m) {
        var sel = m && m.selector; if (!sel) return;
        var els = []; try { els = document.querySelectorAll(sel); } catch (e) { return; }
        var active = sel === self.activeMaskSel;
        for (var i = 0; i < els.length; i++) {
          var R = els[i].getBoundingClientRect();
          if (R.width < 2 || R.height < 2) continue;
          out.push({ active: active, style: { left: (R.left - 2) + "px", top: (R.top - 2) + "px", width: (R.width + 4) + "px", height: (R.height + 4) + "px" } });
        }
      });
      // draw active (green) ones LAST so they sit on top of the faint ones
      out.sort(function (a, b) { return (a.active === b.active) ? 0 : (a.active ? 1 : -1); });
      this.maskStyles = out;
    },
    // Click a block chip → toggle a green outline on the element(s) it hides.
    toggleMaskHighlight(sel) {
      this.activeMaskSel = (this.activeMaskSel === sel) ? "" : sel;
      this.refreshEditMasks();
    },
    // While the edit card is open, draw the step's extra highlights as persistent
    // dashed rings (not just the brief pick-flash) so an added highlight stays
    // visible — that's why "Add another highlight" looked like it never added.
    refreshEditRings() {
      if (!this.editing) { this.ringStyles = []; return; }
      var self = this, step = this.steps[this.editing.idx];
      var rings = [];
      // When editing an EXTRA (areaIndex>=0), the primary target is no longer the
      // adjustable box → draw it as a clickable ring so it stays visible, punches
      // through the dim, and can be clicked to switch back to editing it.
      if (this.editing.areaIndex >= 0 && step && step.target) {
        var pel = looseResolve(step.target);
        if (pel) {
          var pr = boxedRect(pel.getBoundingClientRect(), step.targetBox, 4);
          if (pr.width > 2 && pr.height > 2) rings.push({ style: { left: pr.left + "px", top: pr.top + "px", width: pr.width + "px", height: pr.height + "px" }, hi: "primary" });
        }
      }
      ((step && step.highlights) || []).forEach(function (h, hi) {
        if (self.editing.areaIndex === hi) return;   // that one is the adjustable box
        var el = looseResolve(h.selector);
        if (!el) return;
        var rect = boxedRect(el.getBoundingClientRect(), h.box, 4);
        if (rect.width > 2 && rect.height > 2) rings.push({ style: self.ringStyleFor(h, rect), hi: hi });
      });
      this.ringStyles = rings;
    },
    okEdit() {
      var ed = this.editing;
      if (!ed) return;
      this._clearEditWatch();
      // Target wasn't on the page (targetMissing) — there's no box to store, so
      // just persist the narration the admin edited and close (masks were saved
      // directly to the step as they were picked).
      if (!ed.el || !ed.box) {
        var st0 = this.steps[ed.idx];
        if (st0) { st0.text = ed.text || ""; st0.audio = ed.audio || null; }
        this.editing = null;
        return;
      }
      // Store the adjusted box as offsets from the element's CURRENT rect so it
      // re-anchors correctly on any customer's screen.
      var R = ed.el.getBoundingClientRect();
      var box = {
        dx: Math.round(ed.box.left - R.left),
        dy: Math.round(ed.box.top - R.top),
        w: Math.round(ed.box.width),
        h: Math.round(ed.box.height),
      };
      var step = this.steps[ed.idx];
      if (step) {
        // Narration typed/recorded in the popup is saved to the step.
        step.text = ed.text || "";
        step.audio = ed.audio || null;
        if (ed.kind === "target") {
          step.target = ed.selector;
          step.targetLabel = ed.label;
          step.targetBox = box;
        } else if (ed.areaIndex >= 0 && step.highlights[ed.areaIndex]) {
          // re-edit of an existing area → update its box in place
          step.highlights[ed.areaIndex].box = box;
        } else {
          var existing = step.highlights.filter(function (h) { return h.selector === ed.selector; })[0];
          if (existing) existing.box = box;
          else step.highlights.push({ selector: ed.selector, label: ed.label, box: box });
        }
      }
      this.endDrag();
      this.editing = null;
      this.ringStyles = []; this.maskStyles = []; this.activeMaskSel = "";
    },
    cancelEdit() { this.stopRecord(); this.endDrag(); this._clearEditWatch(); this.editing = null; this.ringStyles = []; this.maskStyles = []; this.activeMaskSel = ""; },

    // ---- record voice directly to the step (mic in the popup) ----
    async startRecord() {
      if (this.recording || !this.editing) return;
      try {
        this._recStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        this.showNote("Microphone permission is needed to record. You can still type the narration.");
        return;
      }
      this._recChunks = [];
      try {
        this._recMr = new MediaRecorder(this._recStream);
        var self = this;
        this._recMr.ondataavailable = function (e) { if (e.data && e.data.size) self._recChunks.push(e.data); };
        this._recMr.start();
        this.recording = true;
      } catch (e) {
        this.stopRecord();
        this.showNote("Couldn't start recording on this browser.");
      }
    },
    stopRecord() {
      if (!this._recMr) { this._stopMicStream(); this.recording = false; return; }
      var self = this;
      var mr = this._recMr;
      this._recMr = null;
      mr.onstop = function () {
        if (self._recChunks.length) {
          var blob = new Blob(self._recChunks, { type: self._recChunks[0].type || "audio/webm" });
          var reader = new FileReader();
          reader.onloadend = function () { if (self.editing) self.editing.audio = reader.result; };
          reader.readAsDataURL(blob);
        }
        self._stopMicStream();
      };
      try { mr.stop(); } catch (e) { this._stopMicStream(); }
      this.recording = false;
    },
    _stopMicStream() {
      if (this._recStream) {
        try { this._recStream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
        this._recStream = null;
      }
    },

    // ----- "Preview tour" screen recording → guide video -----
    // Best supported WebM codec for MediaRecorder on this browser ("" = let the
    // browser choose its default).
    _pickVideoMime() {
      var cands = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm;codecs=vp9", "video/webm"];
      for (var i = 0; i < cands.length; i++) {
        try { if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(cands[i])) return cands[i]; } catch (e) {}
      }
      return "";
    },
    // Begin capturing the tab. Returns true if the share prompt was accepted.
    // MUST be called from within a user gesture (see previewFrom).
    async _startScreenRecording(mode) {
      if (!this.canRecord) return false;
      var self = this;
      try {
        // preferCurrentTab → Chrome offers THIS tab directly ("Share this tab?").
        // Without it Chrome hides the calling tab from the picker (anti
        // "hall-of-mirrors"), which is why the JC Steele tab wasn't listed.
        // (Do NOT also set selfBrowserSurface — Chrome throws if both are given.)
        var stream = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: 30 },
          audio: mode === "audio",
          preferCurrentTab: true,
        });
        this._screenStream = stream;
        this._screenChunks = [];
        var mime = this._pickVideoMime();
        var opts = { videoBitsPerSecond: 2500000 };   // ~2.5 Mbps keeps uploads modest
        if (mime) opts.mimeType = mime;
        var mr = new MediaRecorder(stream, opts);
        mr.ondataavailable = function (e) { if (e.data && e.data.size) self._screenChunks.push(e.data); };
        mr.onstop = function () { self._finishScreenRecording(); };
        // If the admin ends sharing via the browser's own "Stop sharing" bar,
        // finalize what we have.
        try {
          var vt = stream.getVideoTracks()[0];
          if (vt) vt.onended = function () { if (self._screenMr && self._screenMr.state !== "inactive") { try { self._screenMr.stop(); } catch (e) {} } };
        } catch (e) {}
        this._screenMr = mr;
        mr.start();
        // Pin the recording to the flow/page it STARTED on, so it's saved as e.g.
        // "New Order" no matter where the tour ends up (the URL changes during the
        // walkthrough). Used by _finishScreenRecording for the save target + label.
        this._recFlow = this.flowKey;
        // Mark that an in-tab recording is live. The MediaRecorder itself can't
        // survive a full page reload, so if the builder re-opens at tour-end with
        // this marker set but no live recorder, a reload killed it (rare now that
        // recorded previews SPA-navigate) — we explain instead of saving nothing.
        try { localStorage.setItem("jcs_tour_screen_rec", this.flowKey); } catch (e) {}
        return true;
      } catch (e) {
        // cancelled / denied / unsupported → clean up, caller falls back to plain rehearsal
        try { if (this._screenStream) this._screenStream.getTracks().forEach(function (t) { t.stop(); }); } catch (e2) {}
        try { localStorage.removeItem("jcs_tour_screen_rec"); } catch (e2) {}
        this._screenStream = null; this._screenMr = null; this._screenChunks = [];
        return false;
      }
    },
    // Stop the recorder (its onstop → _finishScreenRecording uploads the blob).
    _stopScreenRecording() {
      var mr = this._screenMr;
      if (!mr) return false;
      try {
        if (mr.state !== "inactive") mr.stop();   // → onstop
        else this._finishScreenRecording();
      } catch (e) { this._finishScreenRecording(); }
      return true;
    },
    _finishScreenRecording() {
      var self = this;
      var mr = this._screenMr; this._screenMr = null;
      var chunks = this._screenChunks || []; this._screenChunks = [];
      // Drop the share tracks so the browser's "sharing" indicator clears.
      try { if (this._screenStream) this._screenStream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
      this._screenStream = null;
      try { localStorage.removeItem("jcs_tour_screen_rec"); } catch (e) {}   // recording is over — clear the reload marker
      if (!chunks.length) {
        this.showNote("The recording was empty — nothing to save. (If the tour changed pages, in-tab recording can’t continue past the reload.)", "err");
        return;
      }
      var type = (mr && mr.mimeType) || (chunks[0] && chunks[0].type) || "video/webm";
      var fmt = type.indexOf("mp4") >= 0 ? "mp4" : "webm";
      var blob = new Blob(chunks, { type: type });
      var mb = Math.max(1, Math.round(blob.size / 1048576));
      // The tour finished — open a review modal so the admin can WATCH the capture
      // (blob preview) before choosing Save or Discard. Replaces the old window.confirm,
      // which can't host a <video>. The actual upload lives in confirmSaveRecording().
      this._revokePendingRec();   // free any prior preview URL
      var url = ""; try { url = URL.createObjectURL(blob); } catch (e) {}
      // Save under the flow the recording STARTED on (e.g. "order"/New Order), not
      // whatever page the tour finished on.
      var recFlow = this._recFlow || this.flowKey;
      this.pendingRec = { label: this.flowLabel, flow: recFlow, blob: blob, url: url, mb: mb, fmt: fmt, saving: false };
    },
    // Save the reviewed recording as this page's guide video (the modal's ✓ Save).
    confirmSaveRecording() {
      var self = this;
      var p = this.pendingRec;
      if (!p || p.saving) return;
      this.$set(this.pendingRec, "saving", true);
      this.showNote("Uploading the recorded tour video… (~" + p.mb + " MB)", "info");
      feapi.saveGuideVideo(p.flow, p.blob).then(function (res) {
        // Backend returns the on-disk path; fall back to the known folder pattern if
        // an older backend build doesn't send it yet.
        var savedFmt = (res && res.format) || p.fmt;
        var where = (res && res.path) || ("…/data/guidetour-video/" + p.flow + "." + savedFmt);
        var purl = ""; try { purl = feapi.getGuideVideoUrl(p.flow); } catch (e) {}
        self.savedInfo = { label: p.label, mb: p.mb, format: savedFmt, path: where, url: purl };
        self.showNote("✓ Saved the “" + p.label + "” guide video (" + p.mb + " MB). Save details are shown below.", "ok");
        self.$root.$emit("guide-tours-updated");   // nudge the Guide widget to re-check its source
        self._revokePendingRec();   // close the modal + free the blob URL
      }).catch(function () {
        if (self.pendingRec) self.$set(self.pendingRec, "saving", false);   // keep the modal open so they can retry
        self.showNote("✕ Couldn't upload the recorded video — the recording is still here, try Save again.", "err");
      });
    },
    // Throw the reviewed recording away (the modal's ✕ Discard).
    discardRecording() {
      if (this.pendingRec && this.pendingRec.saving) return;
      this._revokePendingRec();
      this.showNote("Recording discarded — nothing was saved.", "info");
    },
    // Close the review modal + free the preview blob URL.
    _revokePendingRec() {
      if (this.pendingRec && this.pendingRec.url) { try { URL.revokeObjectURL(this.pendingRec.url); } catch (e) {} }
      this.pendingRec = null;
    },
    // Play the step's narration as it will sound: recorded clip if present,
    // else the browser voice reading the typed text.
    replayEdit() {
      if (!this.editing) return;
      this.resumeCtx();
      if (this.editing.audio) this.playUrl(this.editing.audio);   // recorded clip → Web Audio
      else if (this.editing.text) this.speak(this.editing.text);  // typed → neural via Web Audio
    },
    // Play a recorded clip (data URI or /audio/{id} URL) through Web Audio so it
    // isn't subject to the autoplay block that an <audio> element hits.
    playUrl(url) {
      var self = this;
      var ctx = this.ensureCtx();
      this.resumeCtx();
      if (!ctx) { try { new Audio(url).play(); } catch (e) {} return; }
      fetch(url).then(function (r) { return r.arrayBuffer(); })
        .then(function (ab) { return ctx.decodeAudioData(ab.slice(0)); })   // promise form (matches the working self-test)
        .then(function (buf) {
          self.stopClip();
          var src = ctx.createBufferSource();
          src.buffer = buf; src.connect(ctx.destination); src.start(0); self._curSrc = src;
        })
        .catch(function () { try { new Audio(url).play(); } catch (e) {} });
    },
    // Commit this step's box, then preview the next step (the "Next ▸" walk).
    nextEdit() {
      var idx = this.editing ? this.editing.idx : -1;
      this.okEdit();
      var n = idx + 1;
      if (n >= 0 && n < this.steps.length) {
        var self = this;
        this.$nextTick(function () { self.previewStep(n); });
      }
    },

    // ---- per-step preview: spotlight + speak (the live "what you get") ----
    previewStep(i) {
      var s = this.steps[i];
      if (!s) return;
      this._clearPanelMarks();   // the edit view takes over the on-page highlighting
      // Restore the screen this step was built on (assembly / sub-assembly /
      // filter / wear) so the target element is present. Best-effort + async; the
      // edit box's auto-snap (_watchForEditTarget) picks the target up once the
      // restored parts render, so we don't need to await it here.
      if (s.searchCtx) { try { this.$root.$emit("tour:apply-search-ctx", s.searchCtx); } catch (e) {} }
      // Open the adjustable highlight box FIRST so nothing about audio can block it.
      if (s.target) this.editExisting(i, "target", s.target, s.targetLabel, s.targetBox, -1);
      else this.showSpot(s);
      // Then play (if the clip is already cached) or warm it — fully isolated so
      // an audio hiccup can never break the preview/resize.
      try {
        if (s.text && s.text.trim()) {
          var key = this.voice + "|" + plainText(s.text);
          if (this._bufCache[key]) this.speak(s.text); else this.prefetch(s.text);
        }
      } catch (e) {}
    },
    md(t) { return renderMarkup(t); },
    // Preview of the narration as the CUSTOMER will see it. Wildcard tokens are control
    // tokens (the customer never sees the literal braces) — show each as a small pill
    // here instead of raw "{…}" text so the preview doesn't "read" them.
    narrationPreview(t) {
      return this.md(t || "")
        .replace(/\{wait\}/gi, '<span class="gtb-token-pill">⏸ pause</span>')
        .replace(/\{part\}/gi, '<span class="gtb-token-pill">🏷 part code</span>')
        .replace(/\{count\}/gi, '<span class="gtb-token-pill">🔢 count</span>')
        .replace(/\{\s*part\s+name\s*\}/gi, '<span class="gtb-token-pill">🏷 part name</span>')
        .replace(/\{\s*part\s+price\s*\}/gi, '<span class="gtb-token-pill">💲 part price</span>')
        .replace(/\{\s*part\s+code\s*\}/gi, '<span class="gtb-token-pill">🏷 part code</span>')
        .replace(/\{\s*machine(?:\s*name)?\s*\}/gi, '<span class="gtb-token-pill">🏭 machine name</span>')
        .replace(/\{\s*assembly(?:\s*name)?\s*\}/gi, '<span class="gtb-token-pill">🧩 assembly name</span>')
        .replace(/\{showif:\s*hasdrawing\s*\}/gi, '<span class="gtb-token-pill">👁 only if drawing</span>')
        .replace(/\{showif:\s*nodrawing\s*\}/gi, '<span class="gtb-token-pill">🚫 only if no drawing</span>')
        .replace(/\{showif:\s*hasassembly\s*\}/gi, '<span class="gtb-token-pill">👁 only if assemblies</span>')
        .replace(/\{showif:\s*noassembly\s*\}/gi, '<span class="gtb-token-pill">🚫 only if no assemblies</span>');
    },
    editExisting(idx, kind, selector, label, savedBox, areaIndex) {
      this._clearEditWatch();
      var el = looseResolve(selector);   // recover the control even if an old inner-node selector broke
      this.editing = {
        idx: idx, kind: kind, selector: selector, label: label, el: el || null,
        areaIndex: (areaIndex == null ? -1 : areaIndex),
        text: (this.steps[idx] && this.steps[idx].text) || "",
        audio: (this.steps[idx] && this.steps[idx].audio) || null,
        box: null, elRect: null, cardLeft: null, cardTop: null, targetMissing: !el,
        savedBox: savedBox || null,
      };
      if (el) {
        var t = this._applyEditTarget(el, savedBox);
        this.editing.box = t.box;
        this.editing.elRect = t.elRect;
      } else {
        // Deep step whose target only appears after selecting a machine/assembly:
        // open the card (narration + masks editable) AND WATCH the page — the
        // moment the element shows up, the highlight box snaps onto it
        // automatically (no second Preview click needed).
        this.showNote("This step's highlight isn't on the page yet — select the machine/assembly so it appears and the box will snap on automatically. Narration & masks are editable now.", "info");
        this._watchForEditTarget(selector, savedBox);
      }
      this.activeMaskSel = "";
      this.refreshEditRings();   // show this step's existing extra highlights as rings
      this.refreshEditMasks();   // …and its click-blocks as faint overlays
    },
    // Compute the highlight box for an on-page element (instant scroll so the rect
    // is final; saved-offset; clamped on-screen). Returns { box, elRect }.
    _applyEditTarget(el, savedBox) {
      try { el.scrollIntoView({ behavior: "auto", block: "center" }); } catch (e) {}
      var R = el.getBoundingClientRect();
      // Saved offset, but snap back to outline the element if the offset would
      // land the box far from it (stale/wrong capture) — never far off the target.
      var b0 = boxedRect(R, savedBox, 0);
      var box = { left: Math.round(b0.left), top: Math.round(b0.top), width: Math.round(b0.width), height: Math.round(b0.height) };
      var vw = window.innerWidth, vh = window.innerHeight;
      box.left = Math.min(Math.max(box.left, 40 - box.width), vw - 40);
      box.top = Math.min(Math.max(box.top, 8), vh - 40);
      return { box: box, elRect: { left: R.left, top: R.top } };
    },
    // While the edit card is open on a not-yet-present target, poll for it; when
    // it appears (admin navigated the page), snap the box on + drop the warning.
    _watchForEditTarget(selector, savedBox) {
      this._clearEditWatch();
      var self = this;
      this._editWatch = setInterval(function () {
        if (!self.editing || !self.editing.targetMissing || self.editing.selector !== selector) { self._clearEditWatch(); return; }
        var el = looseResolve(selector);
        if (el && el.getBoundingClientRect().width > 2 && el.getBoundingClientRect().height > 2) {
          self._clearEditWatch();
          var t = self._applyEditTarget(el, savedBox);
          self.editing.el = el;
          self.editing.box = t.box;
          self.editing.elRect = t.elRect;
          self.editing.targetMissing = false;
          self.showNote("Found it — drag or resize the highlight now.", "ok");
        }
      }, 400);
    },
    _clearEditWatch() { if (this._editWatch) { clearInterval(this._editWatch); this._editWatch = null; } },
    rectStyle(R, box, pad) {
      if (!R || R.width < 1) return null;
      var rect = boxedRect(R, box, pad);   // saved offset, but snap to the element if it'd land far off
      return { left: rect.left + "px", top: rect.top + "px", width: rect.width + "px", height: rect.height + "px" };
    },
    // Preview tour: ask which step to begin the rehearsal on (overlay list), then
    // play from there. Only one step → just play it (no need to ask).
    openStartPicker() {
      if (!this.hasContent) return;
      this.showVersions = false;
      this.recordMode = "off";   // opt in to recording each time; never auto-record
      this.showStartPicker = true;
    },
    // The admin clicked "Start here"/"Record from here". getDisplayMedia REQUIRES a
    // live user gesture, so when recording is requested we must call it BEFORE the
    // async voice-synth in previewAll (an await there would consume the gesture).
    async previewFrom(i) {
      this.showStartPicker = false;
      if (this.recordMode !== "off" && this.canRecord) {
        var ok = await this._startScreenRecording(this.recordMode);
        if (!ok) { this.showNote("Recording was cancelled — rehearsing without saving a video.", "info"); }
      }
      this.previewAll(i);
    },
    // After a Preview tour ends, the widget records which step it was on
    // (localStorage.jcs_tour_builder_focus_step); scroll the panel back to that
    // step + briefly highlight it, so previewing from step 10 returns you to step
    // 10 — not the top (step 1).
    focusBuilderStep() {
      var self = this;
      var raw = null;
      try { raw = localStorage.getItem("jcs_tour_builder_focus_step"); localStorage.removeItem("jcs_tour_builder_focus_step"); } catch (e) {}
      if (raw == null || raw === "") return;
      var i = parseInt(raw, 10);
      if (isNaN(i) || i < 0) return;
      this.$nextTick(function () {
        try {
          var els = self.$el && self.$el.querySelectorAll ? self.$el.querySelectorAll(".gtb-steps .gtb-step") : null;
          var el = els && els[i];
          if (!el) return;
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("gtb-step-focus");
          setTimeout(function () { try { el.classList.remove("gtb-step-focus"); } catch (e) {} }, 2200);
        } catch (e) {}
      });
    },
    stepSnippet(s) {
      var t = plainText((s && s.text) || "").trim();
      if (!t) return (s && (s.targetLabel || s.target)) || "(no narration)";
      return t.length > 64 ? t.slice(0, 64) + "…" : t;
    },
    previewAll(startIndex) {
      // Preview plays the CURRENT draft directly so it reflects unsaved edits,
      // and it must NOT create a server version — pass the steps inline.
      var self = this;
      var start = startIndex || 0;   // which step to begin the preview on (0 = first)
      var clean = this.cleanSteps();
      if (!clean.length) { this.showNote("Add a step before previewing.", "err"); return; }
      try {
        localStorage.setItem("jcs_tour_rec_" + this.flowKey, JSON.stringify({ flow: this.flowKey, steps: clean }));
      } catch (e) {}
      // If we're RECORDING this run, hide the builder panel NOW (before the
      // ~2s voice pre-synth) so the captured video shows a clean page from the
      // start instead of the builder UI + "generating voice" note.
      var recording = !!this._screenMr;
      if (recording) this.open = false;
      // Pre-synthesize each step's neural voice so the rehearsal sounds exactly
      // like the published tour (steps with no clip fall back to the browser
      // voice during playback). Recorded clips are kept as-is.
      else this.showNote("Generating the narration voice…", "info");
      var jobs = clean.map(function (s) {
        // KEEP an existing clip — do NOT replace it on preview. Synthesize ONLY a
        // step that has no clip yet. To refresh a step's voice, REMOVE it on the
        // step; it then re-synthesizes here (and on Save). Skip text-less steps.
        if (s.audio || !s.text) return Promise.resolve(s);
        return feapi.ttsPreview(s.text, self.voice)
          .then(function (blob) { return blob ? Object.assign({}, s, { audio: URL.createObjectURL(blob) }) : s; })
          .catch(function () { return s; });
      });
      Promise.all(jobs).then(function (playSteps) {
        self.note = ""; self.noteKind = "";
        self.open = false;
        // let the panel close, then play the guided tour over the page (a touch
        // longer when recording so the panel is fully gone before the first frame)
        setTimeout(function () { self.$root.$emit("start-guide-tour", { flow: self.flowKey, steps: playSteps, preview: true, resumeStep: start, recording: recording }); }, recording ? 400 : 200);
      });
    },

    showSpot(step) {
      var self = this;
      var el = step.target ? looseResolve(step.target) : null;
      if (el) {
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) {}
        this.spot = this.rectStyle(el.getBoundingClientRect(), step.targetBox, 8);
      } else {
        this.spot = null;
      }
      var rings = [];
      (step.highlights || []).forEach(function (h) {
        var hel = looseResolve(h.selector);
        if (hel) {
          var rect = boxedRect(hel.getBoundingClientRect(), h.box, 4);
          if (rect.width > 2 && rect.height > 2) rings.push({ style: self.ringStyleFor(h, rect), hi: null });   // preview rings aren't clickable (hi:null)
        }
      });
      this.ringStyles = rings;
      if (this._spotT) clearTimeout(this._spotT);
      // Don't wipe the rings if the edit card is open — there refreshEditRings owns
      // them and they must persist (else an added highlight vanishes after 3.6s).
      this._spotT = setTimeout(function () { if (!self.editing) { self.spot = null; self.ringStyles = []; } }, 3600);
    },
    q(sel) { try { return document.querySelector(sel); } catch (e) { return null; } },

    onVoiceChange() {
      try { localStorage.setItem("jcs_tour_voice", this.voice); } catch (e) {}
      this._bufCache = {};   // re-synthesize previews in the new voice
      this.resumeCtx();      // this change is a user gesture — keep audio unlocked
      this.prefetchAll();    // re-warm every step in the new voice
    },
    // Warm (download + DECODE) every step's clip so 🔊 Play has a ready buffer.
    prefetchAll() {
      var self = this;
      (this.steps || []).forEach(function (s) {
        if (s && s.text && s.text.trim() && !s.audio) self.prefetch(s.text);
      });
    },
    // ---- Web Audio playback — immune to the autoplay policy that blocks an
    // <audio> element's .play(). Once the context is resumed by ANY click, we can
    // start decoded buffers freely (no per-play user-gesture requirement). ----
    ensureCtx() {
      try {
        if (!this._actx) {
          var C = window.AudioContext || window.webkitAudioContext;
          if (C) this._actx = new C();
        }
      } catch (e) {}
      return this._actx;
    },
    resumeCtx() {
      try { var c = this.ensureCtx(); if (c && c.state === "suspended") c.resume(); } catch (e) {}
    },
    // Fetch + DECODE the clip into an AudioBuffer (no playback). Decoding works on
    // a suspended context, so this can run before any gesture.
    prefetch(text) {
      var self = this;
      var clean = plainText(text);
      if (!clean) return;
      var key = this.voice + "|" + clean;
      if (this._bufCache[key]) return;
      feapi.ttsPreview(text, this.voice)
        // null = this voice/clip didn't synthesize — skip caching it; do NOT
        // disable the others (that was the cascade bug).
        .then(function (blob) { return blob ? blob.arrayBuffer() : null; })
        .then(function (ab) {
          if (!ab) return null;
          var ctx = self.ensureCtx();
          // promise form + a buffer copy — matches the working audio self-test.
          return ctx ? ctx.decodeAudioData(ab.slice(0)) : null;
        })
        .then(function (buf) { if (buf) self._bufCache[key] = buf; })
        .catch(function () {});
    },
    // ---- type → voice ----
    speak(text) {
      var self = this;
      var clean = plainText(text);
      if (!clean) return;
      this.resumeCtx();   // the Play/Preview click resumes the context
      var ctx = this.ensureCtx();
      var FALLBACK = "en-US-AriaNeural";   // universally-available neural voice (same one the audio self-test uses)
      // Synthesize + decode + play one voice via Web Audio. If the chosen voice
      // produces NO audio (not available on this Azure resource/tier — e.g. a newer
      // voice on the Free F0 tier), fall back ONCE to the default neural voice so the
      // Play button is never silent. Still neural — never the flat browser voice.
      var tryVoice = function (voice, isFallback) {
        var key = voice + "|" + clean;
        if (self._bufCache[key]) { self.stopClip(); self.playBuffer(key); return; }
        feapi.ttsPreview(text, voice).then(function (blob) {
          if (!blob || !ctx) {
            if (!isFallback && voice.toLowerCase() !== FALLBACK.toLowerCase()) {
              self.showNote("“" + self.voiceLabel + "” isn’t available on this voice service — playing in Aria instead. Pick another voice in the 🔊 list to change it.", "info");
              tryVoice(FALLBACK, true);
            }
            return null;   // neural unavailable → SILENT, never the browser voice
          }
          // promise form + a buffer copy — matches the working audio self-test.
          return blob.arrayBuffer()
            .then(function (ab) { return ctx.decodeAudioData(ab.slice(0)); })
            .then(function (buf) { self._bufCache[key] = buf; self.stopClip(); self.playBuffer(key); });
        }).catch(function () {});   // synth/decode failed → SILENT, never the browser voice (per user request)
      };
      tryVoice(this.voice, false);
    },
    playBuffer(key) {
      var ctx = this.ensureCtx();
      if (!ctx || !this._bufCache[key]) return;
      this.resumeCtx();
      try {
        var src = ctx.createBufferSource();
        src.buffer = this._bufCache[key];
        src.connect(ctx.destination);
        src.start(0);
        this._curSrc = src;
      } catch (e) {}
    },
    stopClip() {
      try { if (this._curSrc) { this._curSrc.stop(); this._curSrc.disconnect(); } } catch (e) {}
      this._curSrc = null;
    },
    _browserSpeak(clean) {
      if (!window.speechSynthesis) return;
      try {
        window.speechSynthesis.cancel();
        var u = new SpeechSynthesisUtterance(clean);
        u.rate = 1; u.pitch = 1;
        var voices = window.speechSynthesis.getVoices() || [];
        var v = voices.filter(function (x) { return /en[-_]US/i.test(x.lang) && /female|samantha|zira|aria|jenny|nova/i.test(x.name); })[0]
          || voices.filter(function (x) { return /^en/i.test(x.lang); })[0];
        if (v) u.voice = v;
        window.speechSynthesis.speak(u);
      } catch (e) {}
    },
    stopSpeak() {
      try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (e) {}
      this.stopClip();
    },

    flashEl(el) {
      try {
        var r = el.getBoundingClientRect();
        this.spot = { left: r.left - 6 + "px", top: r.top - 6 + "px", width: r.width + 12 + "px", height: r.height + 12 + "px" };
        var self = this;
        if (this._spotT) clearTimeout(this._spotT);
        this._spotT = setTimeout(function () { self.spot = null; }, 1100);
      } catch (e) {}
    },

    // Build the persistable step list (drops empty placeholder steps, trims).
    cleanSteps() {
      return this.savableSteps.map(function (s) {
        return {
          text: (s.text || "").trim(),
          audio: s.audio || null,
          target: s.target || null,
          targetLabel: s.targetLabel || "",
          name: (s.name || "").trim(),
          targetBox: s.targetBox || null,
          type: s.type || null,
          showNext: !!s.showNext,
          resultMsg: s.resultMsg || null,
          searchCtx: s.searchCtx || null,
          highlights: (s.highlights || []).map(function (h) { return { selector: h.selector, label: h.label, box: h.box || null, hlStyle: h.hlStyle || null }; }),
          masks: (s.masks || []).map(function (m) { return { selector: m.selector, label: m.label || "" }; }),
        };
      });
    },
    // Export the current tour to a JSON file. Used to PROMOTE a tour across
    // environments (e.g. Sandbox → Production): Export here, then Import + Save in
    // the other environment. Audio is NOT exported — the target env re-synthesizes
    // neural voice from the text on Save, so the file stays small + env-agnostic.
    // ----- selective EXPORT -----
    // Open the "pick which steps to export" modal (all checked by default).
    openExport() {
      if (!this.cleanSteps().length) { this.showNote("Add at least one step before exporting.", "err"); return; }
      this.ieExport = { sel: this.savableSteps.map(function () { return false; }) };   // start DESELECTED; keyed to the NUMBERED (savable) steps
    },
    ieToggleAll(on) {
      if (this.ieExport) this.ieExport.sel = this.savableSteps.map(function () { return !!on; });
    },
    // Download ONLY the checked steps, so you can move a subset into another tour.
    doExport() {
      var self = this;
      var sel = (this.ieExport && this.ieExport.sel) || [];
      var clean = this.cleanSteps();   // 1:1 with savableSteps (the numbered steps), in order
      var picked = [];
      clean.forEach(function (c, i) {
        if (sel[i]) { c.__srcNum = i + 1; c.__srcFlow = self.flowKey; picked.push(c); }   // stamp which source step it was
      });
      if (!picked.length) { this.showNote("Pick at least one step to export.", "err"); return; }
      var payload = { kind: "jcs-guide-tour", flow: this.flowKey, voice: this.voice, steps: picked };
      try {
        var blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
        var url = URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url;
        a.download = "tour-" + this.flowKey + (picked.length < clean.length ? "-" + picked.length + "steps" : "") + ".json";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
        this.ieExport = null;
        this.showNote("✓ Exported " + picked.length + " step" + (picked.length === 1 ? "" : "s") + ". Open the other tour, click Import, and choose what each step replaces.", "ok");
      } catch (e) { this.showNote("✕ Couldn't export.", "err"); }
    },
    // ----- IMPORT with a replace/append mapping -----
    // Read the file, then open a dialog asking — per incoming step — whether to ADD it
    // as a new step or REPLACE a chosen existing step. Server audio paths are dropped
    // (the target tour re-voices on Save in ITS own voice); portable data: clips kept.
    importTour(e) {
      var self = this;
      var input = e && e.target;
      var file = input && input.files && input.files[0];
      if (!file) return;
      var reset = function () { try { input.value = ""; } catch (x) {} };
      var reader = new FileReader();
      reader.onload = function () {
        var data = null;
        try { data = JSON.parse(reader.result); } catch (x) {}
        var steps = data && (data.steps || (Array.isArray(data) ? data : null));
        if (!steps || !steps.length) { self.showNote("✕ That file isn't a valid tour export (no steps).", "err"); reset(); return; }
        var portable = steps.map(function (s) {
          var copy = {};
          for (var k in s) { if (Object.prototype.hasOwnProperty.call(s, k)) copy[k] = s[k]; }
          if (copy.audio && typeof copy.audio === "string" && copy.audio.indexOf("data:") !== 0) copy.audio = null;
          return copy;
        });
        // Open the mapping modal — map: "" = add as new; a number = replace that existing step index.
        self.ieImport = {
          rows: portable.map(function (s) {
            // Default each incoming step to REPLACE the SAME-numbered step in this tour
            // (e.g. exported step 4 → replace step 4), if it exists; otherwise add new.
            var def = "";
            var n = s.__srcNum;
            if (n && self.savableSteps[n - 1]) def = self.steps.indexOf(self.savableSteps[n - 1]);
            return { step: s, map: def };
          }),
          voice: data.voice || null,
          flow: data.flow || null,
          name: file.name || "import",
        };
        reset();
      };
      reader.onerror = function () { self.showNote("✕ Couldn't read that file.", "err"); reset(); };
      reader.readAsText(file);
    },
    // Apply the import per the admin's per-step choices (append or replace).
    applyImport() {
      var self = this;
      var imp = this.ieImport;
      if (!imp || !imp.rows.length) { this.ieImport = null; return; }
      var added = 0, replaced = 0;
      imp.rows.forEach(function (r) {
        var norm = self._normalizeStep(r.step);
        if (typeof r.map === "number" && r.map >= 0 && r.map < self.steps.length) {
          self.$set(self.steps, r.map, norm); replaced++;
        } else {
          self.steps.push(norm); added++;
        }
      });
      // NOTE: do NOT change this tour's voice — imported steps re-voice in the
      // target tour's own voice on Save (that's the point of a cross-tour merge).
      this.ieImport = null;
      this.persistDraft();
      var parts = [];
      if (replaced) parts.push(replaced + " replaced");
      if (added) parts.push(added + " added");
      this.showNote("✓ Imported — " + parts.join(", ") + ". Review, then Save tour to publish here.", "ok");
    },
    // One-line summary of a step for the export/import pickers.
    stepSummary(s) {
      if (!s) return "(empty)";
      var t = plainText(String(s.text || "")).replace(/\{[^}]*\}/g, "").replace(/\s+/g, " ").trim();
      if (t) return t.length > 64 ? t.slice(0, 64) + "…" : t;
      if (s.name) return s.name;
      if (s.targetLabel || s.target) return "▢ " + (s.targetLabel || s.target);
      return "(no narration)";
    },
    // Explicit "Save tour" → publish a NEW version to the DB (Master-only) and
    // confirm the outcome. This is the ONLY path that mints a version; preview
    // does not (see previewAll), so the version advances only on a real save.
    save() {
      var clean = this.cleanSteps();
      if (!clean.length) { this.showNote("Add at least one step before saving.", "err"); return; }
      try {
        localStorage.setItem("jcs_tour_rec_" + this.flowKey, JSON.stringify({ flow: this.flowKey, steps: clean }));
      } catch (e) {}
      var self = this;
      this.saving = true;
      feapi.saveGuideTour(this.flowKey, { steps: clean, voice: this.voice }).then(function (res) {
        self.saving = false;
        if (res && res.version) self.serverVersion = res.version;
        // Reflect the clips the server just synthesized/kept into the draft, so each
        // step shows its voice and the NEXT save REUSES the clip instead of
        // re-synthesizing it. savableSteps shares object refs with steps, so this
        // preserves searchCtx + every other per-step field. (First save attaches a
        // clip to a clip-less step; afterwards it's never touched until Removed.)
        if (res && res.audioUrls && res.audioUrls.length) {
          var sv = self.savableSteps;
          res.audioUrls.forEach(function (url, i) { if (url && sv[i]) self.$set(sv[i], "audio", url); });
          self.persistDraft();
        }
        self.$root.$emit("guide-tours-updated");
        var voiced = (res && res.voiced) ? " · voiced with " + self.voiceLabel : "";
        self.showNote("✓ Saved as version v" + (self.serverVersion || "?") + " — " + clean.length +
          (clean.length === 1 ? " step" : " steps") + voiced + ". Every customer now gets this tour.", "ok");
      }).catch(function (err) {
        self.saving = false;
        var status = err && err.response && err.response.status;
        var msg;
        if (status === 401 || status === 403) msg = "✕ Save failed — sign in as an admin (Master) to publish a tour. Saved on this device only.";
        else if (status) msg = "✕ Server save failed (HTTP " + status + "). Saved on this device only.";
        else msg = "✕ Couldn't reach the server — saved on this device only (the tour backend may not be deployed).";
        self.showNote(msg, "err");
      });
    },
    startOver() {
      if (this.hasContent && !window.confirm(
        "Start over and clear all " + this.savableSteps.length + " step(s) currently in the " + this.flowLabel + " builder?\n\n" +
        "Your SAVED versions are NOT affected — you can restore them from “Versions.” Only unsaved edits in the builder will be lost."
      )) return;
      this.cancelPick(); this.stopSpeak(); this.steps = [this.blankStep()];
    },
    // ===== Copy another flow's tour into this one (to tweak) =====
    // The New Order / New Quote / Search tours are mostly similar, so an admin can
    // start one flow's tour from another. Lists the OTHER flows that have a saved
    // tour; copying pulls their steps into THIS draft to tweak + Save.
    loadCopyOptions() {
      var self = this;
      feapi.getGuideTours().then(function (all) {
        var labels = { order: "New Order tour", quote: "New Quote tour", search: "Search tour" };
        var opts = [];
        Object.keys(all || {}).forEach(function (flow) {
          if (flow === self.flowKey) return;
          var rec = all[flow];
          if (rec && rec.steps && rec.steps.length) {
            opts.push({ flow: flow, label: (labels[flow] || flow) + " · " + rec.steps.length + (rec.steps.length === 1 ? " step" : " steps") });
          }
        });
        self.copyOptions = opts;
      }).catch(function () { self.copyOptions = []; });
    },
    copyFromTour() {
      var self = this;
      var src = this.copySource;
      if (!src) return;
      var srcLabel = ({ order: "New Order", quote: "New Quote", search: "New Search" })[src] || src;
      if (this.hasContent && !window.confirm("Replace the current " + this.flowLabel + " steps with a copy of the “" + srcLabel + "” tour? You can tweak them before saving.")) return;
      feapi.getGuideTours().then(function (all) {
        var rec = all && all[src];
        if (!rec || !rec.steps || !rec.steps.length) { self.showNote("That tour has no steps to copy.", "err"); return; }
        // Deep-copy the source steps but DROP each step's audio clip — THIS flow
        // synthesizes its own voice on save (source clips are tied to the source
        // flow + its text). Everything else carries over (text/target/box/
        // highlights/masks/type/showNext/resultMsg/searchCtx) for tweaking.
        var copied = rec.steps.map(function (s) {
          var c = JSON.parse(JSON.stringify(s));
          c.audio = null;
          return c;
        });
        self.loadIntoSteps({ steps: copied });
        self.persistDraft();
        self.copySource = "";
        self.showNote("✓ Copied " + copied.length + (copied.length === 1 ? " step" : " steps") + " from the “" + srcLabel + "” tour. Tweak them, then Save tour.", "ok");
      }).catch(function () { self.showNote("Couldn't load that tour to copy.", "err"); });
    },
    close() {
      this.cancelPick(); this.stopSpeak(); this._clearEditWatch(); this.editing = null; this.open = false;
      try { localStorage.removeItem("jcs_tour_build_session"); } catch (e) {}  // end the cross-page session
    },
    // ---- version history (review / restore an older saved version) ----
    openVersions() {
      var self = this;
      this.showVersions = true;
      this.versionsLoading = true;
      feapi.getTourVersions(this.flowKey).then(function (list) {
        self.versionList = list || [];
        self.versionsLoading = false;
      }).catch(function () { self.versionList = []; self.versionsLoading = false; });
    },
    loadVersion(v) {
      if (!v || !v.steps) return;
      this.loadIntoSteps({ steps: v.steps });
      this.prefetchAll();
      this.showVersions = false;
      this.showNote("Loaded v" + v.version + " — review it, then Save tour to make it the current version.", "ok");
    },
    fmtDate(s) {
      try {
        var d = new Date(s);
        if (isNaN(d.getTime())) return s || "";
        return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      } catch (e) { return s || ""; }
    },
    // Cache the working draft to localStorage (survives the nav page reload).
    // Falls back to dropping recorded-audio data URIs if the quota is hit.
    persistDraft() {
      if (!this.open || !this.flowKey) return;
      var slim = this.steps.map(function (s) {
        return {
          text: s.text || "", audio: s.audio || null, target: s.target || null,
          targetLabel: s.targetLabel || "", targetBox: s.targetBox || null, name: s.name || "",
          type: s.type || null, showNext: !!s.showNext, resultMsg: s.resultMsg || "",   // keep the per-step flags or the deep-watch draft drops them
          masks: (s.masks || []),
          highlights: (s.highlights || []),
        };
      });
      try {
        localStorage.setItem("jcs_tour_rec_" + this.flowKey, JSON.stringify({ flow: this.flowKey, steps: slim }));
      } catch (e) {
        try {
          slim.forEach(function (s) { s.audio = null; });
          localStorage.setItem("jcs_tour_rec_" + this.flowKey, JSON.stringify({ flow: this.flowKey, steps: slim }));
        } catch (e2) {}
      }
    },
    showNote(m, kind) {
      var self = this;
      this.note = m;
      this.noteKind = kind || "info";
      if (this._noteT) clearTimeout(this._noteT);
      var dur = kind === "ok" ? 6000 : kind === "err" ? 8000 : 5000;
      this._noteT = setTimeout(function () { self.note = ""; self.noteKind = ""; }, dur);
    },
  },
};
</script>

<style>
/* ===== TOUR BUILDER — UNSCOPED, namespaced .gtb-* ===== */
.gtb-spot {
  position: fixed; z-index: 19000; border-radius: 10px; pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(17, 17, 17, 0.45); border: 2px solid #c0392b;
  transition: all 0.3s ease;
}
.gtb-ring {
  /* Extra highlights: same red-dashed design as the primary box (.gtb-edit). They
     punch through the dim because .gtb-editdim is tiled AROUND every highlight
     (the adjustable box + every ring), so no single box-shadow is needed. */
  position: fixed; z-index: 19001; border-radius: 6px; pointer-events: none;
  border: 2px dashed #c0392b; background: transparent;
}
/* While editing, a ring is clickable → click it to make THAT highlight the
   adjustable box (so it can be moved/resized). */
.gtb-ring.is-clickable { pointer-events: auto; cursor: pointer; }
/* Live on-page preview of a highlight while its style box is open (the inline style
   from ringStyleFor drives the border/fill/thickness; this just positions it). */
.gtb-stylepreview { position: fixed; z-index: 19006; pointer-events: none; border-radius: 6px; box-sizing: border-box; }
/* Highlight ANIMATIONS (ring in the builder + the live preview). currentColor = the
   highlight's border color (set inline) so glow/sonar match the chosen color. */
.gtb-anim-glow   { animation: gtbA_glow 1.5s ease-in-out infinite; }
.gtb-anim-sonar  { animation: gtbA_sonar 1.5s ease-out infinite; }
.gtb-anim-pulse  { animation: gtbA_pulse 1.2s ease-in-out infinite; }
.gtb-anim-blink  { animation: gtbA_blink 1s ease-in-out infinite; }
.gtb-anim-bounce { animation: gtbA_bounce 1s ease-in-out infinite; }
.gtb-anim-shake  { animation: gtbA_shake 0.9s ease-in-out infinite; }
@keyframes gtbA_glow   { 0%,100% { box-shadow: 0 0 2px 0 currentColor; } 50% { box-shadow: 0 0 14px 3px currentColor; } }
@keyframes gtbA_sonar  { 0% { box-shadow: 0 0 0 0 currentColor; } 70% { box-shadow: 0 0 0 14px transparent; } 100% { box-shadow: 0 0 0 0 transparent; } }
@keyframes gtbA_pulse  { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
@keyframes gtbA_blink  { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
@keyframes gtbA_bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
@keyframes gtbA_shake  { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-3px); } 75% { transform: translateX(3px); } }
@media (prefers-reduced-motion: reduce) {
  .gtb-anim-glow,.gtb-anim-sonar,.gtb-anim-pulse,.gtb-anim-blink,.gtb-anim-bounce,.gtb-anim-shake { animation: none; }
}
/* Multi-hole page dim for the edit view: strips tiled around ALL highlights (the
   adjustable box + every extra ring) so EVERY highlight punches through — replaces
   the old single box-shadow that could only clear one hole. Below the rings/box so
   they sit on top; pointer-events:none so clicks outside still reach the page. */
.gtb-editdim { position: fixed; z-index: 19000; background: rgba(17, 17, 17, 0.5); pointer-events: none; }
/* "Show on page" outline drawn when a panel chip is clicked (locate a highlight /
   block on the live page without Preview/Resize). Red = highlight, green = block. */
.gtb-pagemark { position: fixed; z-index: 19004; pointer-events: none; border-radius: 6px; border: 2px solid #c0392b; background: rgba(192, 57, 43, 0.12); box-shadow: 0 0 0 1px rgba(192, 57, 43, 0.35); }
.gtb-pagemark.is-block { border-color: #2e9e4f; background: rgba(46, 158, 79, 0.16); box-shadow: 0 0 0 1px rgba(46, 158, 79, 0.4); }
/* Click-BLOCK preview overlays in the edit view: faint gray hatch by default so
   the admin sees what's blocked; bold GREEN when its chip is clicked. */
.gtb-maskpreview {
  position: fixed; z-index: 19002; border-radius: 5px; pointer-events: none;
  border: 1px dashed rgba(120, 120, 120, 0.55); background: rgba(120, 120, 120, 0.10);
}
.gtb-maskpreview.is-active {
  z-index: 19004; border: 3px solid #2e9e4f; background: rgba(46, 158, 79, 0.16);
  box-shadow: 0 0 0 1px rgba(46, 158, 79, 0.4);
}
/* Adjustable highlight box (stays until OK; move + resize). The page dim is now a
   separate multi-hole overlay (.gtb-editdim) so EVERY highlight punches through, not
   just this box — so no box-shadow dim here anymore. */
.gtb-edit {
  position: fixed; z-index: 19008; box-sizing: border-box;
  border: 2px dashed #c0392b; background: transparent; border-radius: 6px;
  /* The WHOLE box is grabbable (cursor:move) so the admin can drag it from
     anywhere — not just the corner ✥ tab, which can land off-screen when the
     box is mispositioned. Only the box rect is interactive; the dimmed surround
     (box-shadow) isn't, so clicks outside it still reach the page. */
  pointer-events: auto; cursor: move;
}
/* While PICKING a block/mask target, the box (and its tab/handle) must be
   click-through so the admin can click the element UNDER it — the drag is
   restored the moment the pick ends. */
.gtb-edit-picking, .gtb-edit-picking * { pointer-events: none !important; }
.gtb-edit-move {
  position: absolute; top: -1px; left: -1px; z-index: 2; pointer-events: auto; cursor: move;
  background: #c0392b; color: #fff !important; font-size: 11px; line-height: 1;
  padding: 3px 6px; border-radius: 5px 0 6px 0; user-select: none;
}
.gtb-edit-handle {
  position: absolute; right: -8px; bottom: -8px; width: 16px; height: 16px; z-index: 2;
  background: #c0392b; border: 2px solid #fff; border-radius: 50%; cursor: nwse-resize;
  pointer-events: auto;   /* re-enable on the resize handle */
}
/* Top-right ✕ on the box → force-delete this highlight (clears a misplaced target). */
.gtb-edit-del {
  position: absolute; top: -1px; right: -1px; z-index: 3; pointer-events: auto; cursor: pointer;
  background: #c0392b; color: #fff !important; font-size: 12px; line-height: 1; font-weight: 800;
  padding: 3px 7px; border-radius: 0 4px 0 6px; user-select: none;
}
.gtb-edit-del:hover { background: #a02b1f; }
/* Narration card shown alongside the adjustable box (read-aloud text + controls). */
.gtb-edit-card {
  position: fixed; z-index: 19009; width: 340px; max-width: calc(100vw - 24px);
  background: #fff; border-radius: 10px; border-top: 3px solid #c0392b;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28); padding: 12px 14px;
}
.gtb-edit-card-step { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #c0392b; margin-bottom: 5px; }
.gtb-card-grip { cursor: move; user-select: none; display: inline-block; padding: 2px 6px; margin: -2px -6px 5px; border-radius: 5px; background: #fbeceb; }
.gtb-card-grip:hover { background: #f6dcd9; }
.gtb-edit-card-input {
  width: 100%; border: 1px solid #d6d6d6; border-radius: 6px; padding: 7px 9px;
  font-size: 13px; line-height: 1.4; resize: vertical; font-family: inherit; margin-bottom: 8px;
}
.gtb-edit-card-input:focus { outline: none; border-color: #c0392b; }
.gtb-edit-card-mic { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; }
.gtb-mic-btn { border: 1px solid #d6d6d6; background: #fff; border-radius: 6px; padding: 5px 11px; font-size: 12px; font-weight: 600; cursor: pointer; }
.gtb-mic-btn:hover { background: #f3f3f3; }
.gtb-mic-btn.is-rec { background: #c0392b; border-color: #c0392b; }
.gtb-mic-has { font-size: 11.5px; color: #2e7d32; font-weight: 600; }
.gtb-mic-x { border: 0; background: none; font-size: 11px; cursor: pointer; padding: 0; }
.gtb-mic-x:hover { text-decoration: underline; }
.gtb-edit-card-text { font-size: 14px; line-height: 1.45; color: #2a2a2a; }
.gtb-edit-card-text.gtb-muted { color: #999; font-style: italic; }
.gtb-edit-card-hint { margin-top: 8px; font-size: 11px; color: #888; }
.gtb-edit-card-row { display: flex; align-items: center; gap: 6px; margin-top: 12px; }
/* Wildcard-token hover help ( { } icon at the bottom of the callout card). */
.gtb-token-help { position: relative; display: inline-flex; align-items: center; cursor: help; outline: none; }
.gtb-token-ico {
  font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; font-weight: 700;
  color: #777 !important; background: #f2f2f2; border: 1px solid #dcdcdc; border-radius: 6px;
  padding: 4px 7px; line-height: 1;
}
.gtb-token-help:hover .gtb-token-ico, .gtb-token-help:focus .gtb-token-ico { background: #e9e9e9; color: #c0392b !important; border-color: #d8b3ae; }
.gtb-token-pop {
  display: none; position: absolute; left: 0; bottom: calc(100% + 8px); z-index: 30;
  width: 270px; background: #fff; border: 1px solid #e0e0e0; border-radius: 8px;
  box-shadow: 0 8px 26px rgba(0, 0, 0, 0.18); padding: 10px 12px;
  font-size: 11.5px; line-height: 1.45; color: #333 !important; text-align: left; cursor: default;
}
.gtb-token-help:hover .gtb-token-pop, .gtb-token-help:focus .gtb-token-pop, .gtb-token-help:focus-within .gtb-token-pop { display: block; }
.gtb-token-pop b { color: #2a2a2a !important; }
.gtb-token-pop span { display: block; margin-top: 6px; color: #444 !important; }
.gtb-token-pop code { font-family: ui-monospace, Menlo, Consolas, monospace; background: #fdeceb; color: #c0392b !important; border-radius: 3px; padding: 0 4px; font-size: 11px; }
.gtb-edit-ok { border: 0; background: #2e7d32; border-radius: 6px; padding: 6px 14px; font-size: 12.5px; font-weight: 700; cursor: pointer; }
.gtb-edit-ok:hover { background: #256628; }
.gtb-edit-next { border: 0; background: #c0392b; border-radius: 6px; padding: 6px 14px; font-size: 12.5px; font-weight: 700; cursor: pointer; }
.gtb-edit-next:hover { background: #a02b1f; }
.gtb-edit-cancel, .gtb-edit-replay { border: 1px solid #d6d6d6; background: #fff; border-radius: 6px; padding: 6px 11px; font-size: 12.5px; cursor: pointer; }
.gtb-edit-cancel:hover, .gtb-edit-replay:hover { background: #f3f3f3; }
.gtb-pickbar {
  position: fixed; z-index: 19010; top: 14px; left: 50%; transform: translateX(-50%);
  background: #1f1f1f; color: #fff; font-size: 13px; font-weight: 600;
  padding: 9px 14px; border-radius: 8px; box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35);
  display: flex; align-items: center; gap: 12px;
}
/* While the Tour Builder session is open (body.gtb-pushed), shift the page out
   from under the docked 380px panel so the page's OWN controls (e.g. the
   "Show wear parts only" toggle) stay visible + clickable. margin on #app
   (width:auto) shrinks it with no horizontal overflow; the panel and the
   tour/widget overlays are viewport-fixed, so they're unaffected. Desktop only —
   on narrow screens the panel is near-full-width so there's no room to push. */
@media (min-width: 992px) {
  /* Do NOT push the page content — the user wants the page card to stay put.
     Only the right-edge FIXED rails need to move: they're viewport-pinned and
     would otherwise hide BEHIND the panel, so shift the INFO/GUIDE/SETUP pills
     (.floating-pill-rail) and the language/font/theme toggles (#utility-toggle-
     stack, in steele-consistency-fix.css) left of the 380px panel so they stay
     visible + clickable while building. Snaps back when the builder closes. */
  body.gtb-pushed .floating-pill-rail { right: 392px; }
  body.gtb-pushed #utility-toggle-stack { right: 400px; }
}
.gtb-panel {
  position: fixed; z-index: 19005; top: 0; right: 0; height: 100vh; width: 380px;
  max-width: 92vw; background: #fff; box-shadow: -8px 0 28px rgba(0, 0, 0, 0.22);
  display: flex; flex-direction: column; border-left: 3px solid #c0392b;
}
.gtb-head {
  display: flex; align-items: center; padding: 14px 16px 8px;
}
.gtb-title { font-weight: 700; font-size: 15px; color: #2a2a2a; }
.gtb-x { margin-left: auto; border: 0; background: none; font-size: 18px; color: #888; cursor: pointer; }
.gtb-x:hover { color: #333; }
.gtb-submeta {
  display: flex; align-items: center; gap: 7px; padding: 0 16px 11px;
  border-bottom: 1px solid #eee; font-size: 11.5px; color: #777;
}
.gtb-ver { font-weight: 700; color: #c0392b; }
.gtb-dot { color: #bbb; }
.gtb-cnt { color: #777; }
.gtb-voicewrap { display: inline-flex; align-items: center; gap: 4px; }
.gtb-voice { font-size: 11px; border: 1px solid #d6d6d6; border-radius: 5px; padding: 1px 4px; background: #fff; color: #2a2a2a; max-width: 160px; }
.gtb-steps { flex: 1 1 auto; overflow-y: auto; padding: 12px 14px; }
.gtb-step { border: 1px solid #e6e6e6; border-radius: 8px; padding: 10px 11px; margin-bottom: 12px; background: #fafafa; }
.gtb-step-focus { animation: gtbStepFocus 2.2s ease-out; }
@keyframes gtbStepFocus {
  0%   { box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.6); border-color: #c0392b; }
  100% { box-shadow: 0 0 0 3px rgba(192, 57, 43, 0); border-color: #e6e6e6; }
}
.gtb-step-top { display: flex; align-items: center; margin-bottom: 6px; }
.gtb-step-n { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #c0392b; white-space: nowrap; }
.gtb-step-name { flex: 1 1 auto; min-width: 0; margin: 0 8px; border: 1px solid #e2e2e2; border-radius: 5px; padding: 3px 7px; font-size: 12px; font-weight: 600; color: #333; background: #fff; }
.gtb-step-name::placeholder { font-weight: 400; color: #b0b0b0; font-style: italic; }
.gtb-step-tools { margin-left: auto; display: flex; gap: 4px; }
.gtb-text {
  width: 100%; border: 1px solid #d6d6d6; border-radius: 6px; padding: 7px 9px;
  font-size: 13px; line-height: 1.4; resize: vertical; font-family: inherit;
}
.gtb-text:focus { outline: none; border-color: #c0392b; }
.gtb-rendered {
  margin-top: 6px; padding: 6px 9px; background: #fff; border: 1px dashed #e0e0e0;
  border-radius: 6px; font-size: 12.5px; line-height: 1.4; color: #333;
}
/* Wildcard tokens ({wait}/{part}/{count}) shown as pills in the preview — the customer
   never sees the literal braces, so the preview shouldn't either. */
.gtb-token-pill { display: inline-block; background: #fff4d6; color: #8a6d1a; border: 1px solid #ecd9a0; border-radius: 10px; padding: 0 7px; font-size: 11px; font-weight: 700; }
.gtb-mark-edit { border: 0; background: none; font-size: 11px; cursor: pointer; padding: 0; }
.gtb-mark-edit:hover { text-decoration: underline; }
.gtb-step-row { display: flex; gap: 6px; margin-top: 8px; }
.gtb-pickbtn {
  flex: 1 1 auto; text-align: left; border: 1px solid #d6d6d6; background: #fff;
  border-radius: 6px; padding: 6px 9px; font-size: 12px; color: #333; cursor: pointer;
}
.gtb-pickbtn:hover { border-color: #c0392b; color: #c0392b; }
/* 👁 "show this highlight on the page" toggle beside the target pick button. */
.gtb-eye {
  flex: 0 0 auto; border: 1px solid #d6d6d6; background: #fff; border-radius: 6px;
  padding: 6px 8px; font-size: 12px; line-height: 1; cursor: pointer;
}
.gtb-eye:hover { border-color: #c0392b; }
/* Active "show on page" = light red tint + red border (NOT a solid red fill, which
   read like a delete button). */
.gtb-eye.is-active { background: #fdeceb; border-color: #c0392b; }
/* The remove-target ✕ — neutral gray, turns red on hover so it clearly reads as
   "remove" (distinct from the 👁 show toggle). */
.gtb-eye-del { color: #b0b0b0; font-weight: 700; }
.gtb-eye-del:hover { color: #c0392b; border-color: #c0392b; background: #fdeceb; }
.gtb-mini {
  border: 1px solid #d6d6d6; background: #fff; border-radius: 6px; padding: 5px 8px;
  font-size: 12px; color: #555; cursor: pointer;
}
.gtb-mini:hover { background: #f2f2f2; }
.gtb-mini:disabled { opacity: 0.4; cursor: not-allowed; }
.gtb-del { color: #c0392b; }
.gtb-marks { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 7px; }
.gtb-mark {
  display: inline-flex; align-items: center; gap: 5px; font-size: 11px; color: #b9770e;
  background: #fff4e0; border: 1px solid #f0c36d; border-radius: 999px; padding: 2px 8px;
}
.gtb-markx { border: 0; background: none; color: #b9770e; cursor: pointer; font-size: 11px; padding: 0; }
.gtb-step-actions { margin-top: 9px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.gtb-droptoggle { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; color: #555; cursor: pointer; user-select: none; }
.gtb-showif-sel { font-size: 11px; border: 1px solid #d6d6d6; border-radius: 5px; padding: 1px 4px; background: #fff; color: #2a2a2a; cursor: pointer; }
.gtb-droptoggle input { margin: 0; }
.gtb-wait-hint {
  margin: 5px 0 2px; padding: 5px 8px; font-size: 10.5px; line-height: 1.4;
  color: #6b5300; background: #fff8e6; border: 1px solid #f0d98a; border-radius: 5px;
}
.gtb-wait-hint b { color: #8a6d00; }
.gtb-result-row { margin-top: 9px; }
.gtb-result-label { display: block; font-size: 11px; font-weight: 700; color: #555; margin-bottom: 3px; }
.gtb-result-hint { font-weight: 400; color: #999; }
.gtb-result-hint code { background: #f0f0f0; border-radius: 3px; padding: 0 3px; font-size: 10.5px; }
.gtb-result-input { width: 100%; border: 1px solid #d6d6d6; border-radius: 6px; padding: 6px 9px; font-size: 12.5px; box-sizing: border-box; }
.gtb-maskbtn { border-style: dashed !important; border-color: #d49a96 !important; color: #a8362c !important; background: #fdf6f5 !important; }
.gtb-masks { margin-top: 6px; display: flex; flex-wrap: wrap; gap: 6px; }
.gtb-mask-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; background: #f7eceb; color: #a8362c; border: 1px solid #e2c4c0; border-radius: 12px; padding: 2px 4px 2px 8px; }
.gtb-mask-chip--click { cursor: pointer; }
.gtb-mask-chip--click:hover { background: #efe1df; }
.gtb-mask-chip.is-active { background: #e7f6ec; color: #1f7a3d !important; border-color: #9ed3b2; }
.gtb-mask-chip.is-active .gtb-mask-x { color: #1f7a3d !important; }
/* The highlight chip whose highlight is the active (adjustable) red box — solid red
   so it reads as "this is the selected red highlight" (distinct from the green
   block-chip active state). */
.gtb-mask-chip.gtb-hl-active { background: #c0392b; color: #fff !important; border-color: #a02b1f; }
.gtb-mask-chip.gtb-hl-active .gtb-mask-x { color: #fff !important; }
.gtb-mask-x { border: 0; background: none; color: #a8362c !important; cursor: pointer; font-size: 11px; line-height: 1; padding: 0 2px; }
.gtb-mask-x:hover { color: #6f201a !important; }
/* Match-count badge on a highlight chip — shown only when the selector matches
   ≠1 elements: amber for >1 (broad selector painting a box on each → the usual
   cause of "extra/overlapping boxes"), red for 0 (highlight isn't on this page). */
.gtb-hl-count { margin-left: 5px; font-size: 9.5px; font-weight: 700; line-height: 1; padding: 1px 4px; border-radius: 8px; background: #f3a712; color: #3a2a00; cursor: help; }
.gtb-hl-count.is-zero { background: #c0392b; color: #fff; }
/* Collapsed-step summary of the ✨ highlights / 🚫 blocks that now live in Preview /
   Resize — muted + compact so the first page stays clean; click to open the editor. */
.gtb-extras-summary { display: inline-flex; align-items: center; flex-wrap: wrap; gap: 4px 10px; margin-top: 6px; padding: 3px 9px; font-size: 11px; color: #6a6a6a; background: #f4f4f5; border: 1px dashed #d4d4d8; border-radius: 7px; cursor: pointer; }
.gtb-extras-summary:hover:not(:disabled) { background: #ececef; border-color: #b9b9c0; color: #444; }
.gtb-extras-summary:disabled { opacity: .55; cursor: default; }
.gtb-extras-h, .gtb-extras-b { white-space: nowrap; }
.gtb-extras-warn { color: #c0392b; margin-left: 3px; cursor: help; font-weight: 700; }
.gtb-extras-cta { color: #9a9aa2; font-style: italic; }
/* 💫 pulse toggle on a highlight chip — dim when off, full when the glow is on. */
/* 🎨 style button on a highlight chip — opens the style box. */
.gtb-style-btn { border: 0; background: none; cursor: pointer; font-size: 11px; line-height: 1; padding: 0 2px; opacity: 0.6; }
.gtb-style-btn:hover { opacity: 1; }
.gtb-style-btn.is-on { opacity: 1; transform: scale(1.15); }
/* Highlight STYLE box — root-level floating panel (works from the panel AND the
   Preview/Resize card). Bottom-left so it clears the right-side panel/card. */
.gtb-styleedit {
  position: fixed; left: 16px; bottom: 16px; z-index: 19011; width: 290px; max-width: calc(100vw - 32px);
  background: #fff; border-radius: 10px; border-top: 3px solid #c0392b; box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
}
.gtb-styleedit-head { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; border-bottom: 1px solid #eee; font-size: 12.5px; font-weight: 700; color: #c0392b; cursor: move; user-select: none; }
.gtb-style-anim-row { margin-bottom: 9px; }
.gtb-style-anims { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 5px; }
.gtb-style-body { padding: 8px 10px 10px; }
.gtb-style-presets { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 10px; }
.gtb-style-preset { border: 1px solid #d6d6d6; background: #fff; border-radius: 12px; padding: 3px 9px; font-size: 11px; color: #444; cursor: pointer; }
.gtb-style-preset:hover { border-color: #c0392b; color: #c0392b; }
.gtb-style-row { display: flex; align-items: center; gap: 8px; margin-bottom: 9px; }
.gtb-style-lbl { flex: 0 0 78px; font-size: 11.5px; font-weight: 600; color: #555; }
.gtb-style-color { width: 40px; height: 26px; border: 1px solid #d6d6d6; border-radius: 5px; background: #fff; padding: 1px; cursor: pointer; }
.gtb-style-range { flex: 1 1 auto; }
.gtb-style-val { flex: 0 0 34px; font-size: 11.5px; color: #333; text-align: right; }
.gtb-style-num { width: 48px; border: 1px solid #d6d6d6; border-radius: 5px; padding: 3px 6px; font-size: 12px; color: #333; }
.gtb-style-times { font-size: 11.5px; color: #666; }
.gtb-style-line { border: 1px solid #d6d6d6; background: #fff; border-radius: 6px; padding: 3px 8px; font-size: 11px; color: #555; cursor: pointer; text-transform: capitalize; }
.gtb-style-line.is-on { background: #c0392b; color: #fff; border-color: #a02b1f; }
.gtb-style-row .gtb-mini.is-on { background: #eee; }
.gtb-style-hint { font-size: 10.5px; color: #888; margin-top: 2px; }
.gtb-edit-masks { margin-top: 9px; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.gtb-edit-maskbtn { border: 1px dashed #d49a96; background: #fdf6f5; color: #a8362c !important; border-radius: 6px; padding: 5px 10px; font-size: 12px; font-weight: 700; cursor: pointer; }
.gtb-edit-maskbtn:hover { background: #f7eceb; }
.gtb-edit-warn { margin-top: 8px; font-size: 11.5px; color: #8a5a00; background: #fff4e0; border: 1px solid #f0c36d; border-radius: 6px; padding: 6px 9px; line-height: 1.4; }
.gtb-play { border: 0; background: #2a2a2a; color: #fff; border-radius: 6px; padding: 6px 12px; font-size: 12px; font-weight: 700; cursor: pointer; }
.gtb-play:hover { background: #000; }
.gtb-play:disabled { opacity: 0.4; cursor: not-allowed; }
.gtb-addstep {
  width: 100%; border: 1px dashed #c0392b; background: #fff; color: #c0392b;
  border-radius: 8px; padding: 9px; font-size: 13px; font-weight: 700; cursor: pointer;
}
.gtb-addstep:hover { background: #fff5f4; }
.gtb-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 12px 14px; border-top: 1px solid #eee; }
.gtb-import-input { display: none; }
.gtb-spacer { flex: 1 1 auto; }
.gtb-ghost { border: 1px solid #d6d6d6; background: #fff; color: #444; border-radius: 6px; padding: 8px 12px; font-size: 12.5px; font-weight: 700; cursor: pointer; }
.gtb-ghost:hover { background: #f5f5f5; }
.gtb-ghost:disabled { opacity: 0.4; cursor: not-allowed; }
.gtb-copy-sel { border: 1px solid #d6d6d6; background: #fff; color: #444; border-radius: 6px; padding: 7px 8px; font-size: 12.5px; font-weight: 700; cursor: pointer; max-width: 170px; }
.gtb-save { border: 0; background: #c0392b; color: #fff; border-radius: 6px; padding: 8px 16px; font-size: 12.5px; font-weight: 700; cursor: pointer; }
.gtb-save:hover { background: #a02b1f; }
.gtb-save:disabled { opacity: 0.4; cursor: not-allowed; }
.gtb-note { padding: 10px 12px; margin: 0 14px 12px; font-size: 12px; font-weight: 600; border-radius: 6px; line-height: 1.4; }
.gtb-note--ok { color: #1f6e23; background: #e7f5e8; border: 1px solid #b6e0b9; }
.gtb-note--err { color: #b3261e; background: #fdeceb; border: 1px solid #f3c0bc; }
.gtb-note--info { color: #2a2a2a; background: #f3f3f3; border: 1px solid #e2e2e2; }
.gtb-saved { margin: 0 14px 12px; padding: 11px 12px; border-radius: 8px; background: #e7f5e8; border: 1px solid #b6e0b9; }
.gtb-saved-h { font-size: 12.5px; font-weight: 800; color: #1f6e23; margin-bottom: 7px; }
.gtb-saved-row { display: flex; gap: 8px; align-items: baseline; font-size: 11.5px; margin-top: 3px; }
.gtb-saved-row > span { flex: 0 0 64px; color: #4a4a4a; font-weight: 700; }
.gtb-saved-row > code { flex: 1 1 auto; min-width: 0; word-break: break-all; color: #1d1d1d; background: #fff; border: 1px solid #cfe6d0; border-radius: 4px; padding: 2px 6px; font-family: ui-monospace, Menlo, Consolas, monospace; }
.gtb-saved-acts { display: flex; gap: 10px; align-items: center; margin-top: 9px; }
.gtb-saved-link { font-size: 11.5px; font-weight: 700; color: #1f6e23; text-decoration: underline; }
.gtb-saved-x { font-size: 11.5px; font-weight: 700; color: #555; background: #fff; border: 1px solid #cfcfcf; border-radius: 5px; padding: 3px 10px; cursor: pointer; }
.gtb-saved-x:hover { background: #f1f1f1; }
.gtb-recmodal { position: fixed; inset: 0; z-index: 21000; background: rgba(17,17,17,.6); display: flex; align-items: center; justify-content: center; padding: 18px; }
.gtb-recmodal-box { width: min(780px, 96vw); max-height: 92vh; overflow: auto; background: #fff; border-radius: 12px; box-shadow: 0 18px 60px rgba(0,0,0,.45); padding: 18px; }
.gtb-recmodal-h { font-size: 16px; font-weight: 800; color: #1d1d1d; }
.gtb-recmodal-sub { font-size: 12.5px; color: #555; margin: 4px 0 12px; }
.gtb-recmodal-vid { width: 100%; max-height: 58vh; background: #000; border-radius: 8px; display: block; }
.gtb-recmodal-meta { font-size: 11.5px; color: #666; margin-top: 9px; font-weight: 600; }
.gtb-recmodal-acts { display: flex; gap: 10px; margin-top: 14px; }
.gtb-recmodal-save { flex: 1 1 auto; font-size: 13px; font-weight: 800; color: #fff !important; background: #1f6e23; border: 1px solid #1a5e1d; border-radius: 8px; padding: 11px 14px; cursor: pointer; }
.gtb-recmodal-save:hover { background: #1a5e1d; }
.gtb-recmodal-save:disabled { opacity: .6; cursor: default; }
.gtb-recmodal-discard { flex: 0 0 auto; font-size: 13px; font-weight: 700; color: #b3261e !important; background: #fff; border: 1px solid #e2b8b3; border-radius: 8px; padding: 11px 16px; cursor: pointer; }
.gtb-recmodal-discard:hover { background: #fdeceb; }
.gtb-recmodal-discard:disabled { opacity: .6; cursor: default; }

/* Export/Import dialogs — reuse the recmodal shell + recmodal-h/sub/acts/discard. */
.gtb-iemodal { position: fixed; inset: 0; z-index: 21000; background: rgba(17,17,17,.6); display: flex; align-items: center; justify-content: center; padding: 18px; }
.gtb-iemodal-box { width: min(660px, 96vw); max-height: 90vh; overflow: auto; background: #fff; border-radius: 12px; box-shadow: 0 18px 60px rgba(0,0,0,.45); padding: 18px; text-align: left; }
/* Red primary action (overrides the green recmodal-save) for these dialogs. */
.gtb-iemodal .gtb-ie-go { background: #b3261e !important; border-color: #8f1c16 !important; color: #fff !important; }
.gtb-iemodal .gtb-ie-go:hover:not(:disabled) { background: #8f1c16 !important; }
/* Select-all toolbar above the list. */
.gtb-ie-toolbar { display: flex; align-items: center; gap: 12px; margin: 12px 0 6px; }
.gtb-ie-selall { font-size: 12px; font-weight: 700; color: #b3261e; background: #fff; border: 1px solid #e2b8b3; border-radius: 6px; padding: 5px 12px; cursor: pointer; }
.gtb-ie-selall:hover { background: #fdeceb; }
.gtb-ie-count { font-size: 11.5px; color: #777; font-weight: 600; }
.gtb-ie-list { border: 1px solid #eee; border-radius: 8px; max-height: 56vh; overflow: auto; }
/* Header + rows share ONE grid so columns line up, and grid forces left layout
   (overrides any inherited flex/right-align from the surrounding portal). */
.gtb-ie-head, .gtb-ie-row { display: grid; grid-template-columns: 24px 26px 1fr; align-items: center; gap: 10px; padding: 8px 12px; text-align: left; }
.gtb-ie-head { position: sticky; top: 0; background: #f6f6f6; border-bottom: 1px solid #e3e3e3; font-size: 10.5px; font-weight: 800; color: #888; text-transform: uppercase; letter-spacing: .04em; z-index: 1; }
.gtb-ie-row { border-bottom: 1px solid #f1f1f1; font-size: 12.5px; color: #2a2a2a; cursor: pointer; }
.gtb-ie-row:last-child { border-bottom: 0; }
.gtb-ie-row input[type="checkbox"] { margin: 0; }
.gtb-ie-num { width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; background: #b3261e; color: #fff; font-size: 11px; font-weight: 800; }
.gtb-ie-num--in { background: #1f6e23; }
.gtb-ie-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* Import mapping row: [incoming step info] [add/replace select]. */
.gtb-ie-maprow { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 10px; padding: 9px 12px; border-bottom: 1px solid #f1f1f1; font-size: 12.5px; text-align: left; }
.gtb-ie-maprow:last-child { border-bottom: 0; }
.gtb-ie-inc { display: flex; align-items: center; gap: 8px; min-width: 0; }
.gtb-ie-sel { max-width: 320px; }
/* Version history overlay (covers the panel body). */
.gtb-versions {
  position: absolute; top: 56px; left: 0; right: 0; bottom: 0; z-index: 5;
  background: #fff; display: flex; flex-direction: column; border-top: 1px solid #eee;
}
.gtb-versions-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 11px 14px; border-bottom: 1px solid #eee; font-weight: 700; font-size: 13px; color: #2a2a2a;
}
.gtb-versions-empty { padding: 18px 14px; color: #888; font-size: 13px; }
.gtb-versions-list { flex: 1 1 auto; overflow-y: auto; padding: 6px 0; }
.gtb-version-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 9px 14px; border-bottom: 1px solid #f2f2f2;
}
.gtb-version-meta { display: flex; flex-direction: column; gap: 2px; }
.gtb-version-n { font-weight: 700; font-size: 13px; color: #c0392b; }
.gtb-version-active {
  display: inline-block; margin-left: 6px; font-size: 9.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.05em; color: #1f6e23; background: #e7f5e8; border-radius: 4px; padding: 1px 5px; vertical-align: middle;
}
.gtb-version-sub { font-size: 11px; color: #888; }
.gtb-versions-foot { padding: 10px 14px; border-top: 1px solid #eee; font-size: 11.5px; color: #777; }
.gtb-versions-head--sub { font-size: 12px; font-weight: 700; color: #555 !important; background: #fafafa; }
/* "Blank new step" button inside the ＋ insert chooser — the full-width .gtb-addstep
   needs side margins so it doesn't run to the overlay edges. */
.gtb-ins-blank { width: auto; display: block; margin: 12px 14px; }

/* Record-this-run options inside the Preview-tour picker.
   Text colors are FORCED (!important): the portal's global theme paints
   label/input text white, so without this only the red bold words showed
   (white-on-white labels + blown-out Bootstrap <label> margins). */
.gtb-rec-opts { padding: 12px 14px 13px; border-bottom: 1px solid #eee; background: #fcfcfd; }
.gtb-rec-opts--off { color: #888 !important; font-size: 11.5px; line-height: 1.45; }
.gtb-rec-title { font-weight: 700; font-size: 12.5px; color: #2a2a2a !important; margin: 0 0 9px; }
.gtb-rec-row {
  display: flex !important; align-items: flex-start; gap: 9px;
  margin: 0 0 7px !important; padding: 0;
  font-size: 12.5px; line-height: 1.4; font-weight: 400;
  color: #333 !important; cursor: pointer; text-align: left;
}
.gtb-rec-row:last-of-type { margin-bottom: 0 !important; }
.gtb-rec-row > span { flex: 1 1 auto; color: #333 !important; }
.gtb-rec-row b { color: #c0392b !important; font-weight: 700; }
.gtb-rec-row input[type="radio"] {
  flex: 0 0 auto !important; width: 15px !important; height: 15px !important;
  min-width: 15px; margin: 1px 0 0 !important; padding: 0;
  accent-color: #c0392b; cursor: pointer;
}
.gtb-rec-hint {
  margin: 10px 0 2px; padding: 8px 10px; background: #fff6f5; border: 1px solid #f3d4cf;
  border-radius: 6px; font-size: 11px; line-height: 1.5; color: #7a4540 !important;
}
.gtb-rec-hint b { color: #c0392b !important; }

/* ===== Readable button text =====
   The portal's global button theme paints these white, making the labels
   invisible on the builder's light buttons. Force dark/brand-colored text
   (and light backgrounds) with !important so every label is readable. */
.guide-tour-builder button { color: #2a2a2a !important; }
.guide-tour-builder .gtb-pickbtn,
.guide-tour-builder .gtb-mini,
.guide-tour-builder .gtb-ghost,
.guide-tour-builder .gtb-play {
  background: #fff !important;
  color: #2a2a2a !important;
  border: 1px solid #c9c9c9 !important;
}
.guide-tour-builder .gtb-play { font-weight: 700; }
.guide-tour-builder .gtb-addstep {
  background: #fff !important;
  color: #c0392b !important;
}
.guide-tour-builder .gtb-save {
  background: #fff !important;
  color: #c0392b !important;
  border: 2px solid #c0392b !important;
}
.guide-tour-builder .gtb-del { color: #c0392b !important; }
.guide-tour-builder .gtb-x { color: #888 !important; }
.guide-tour-builder .gtb-mark,
.guide-tour-builder .gtb-mark-edit,
.guide-tour-builder .gtb-markx { color: #b9770e !important; }
/* The dark "picking…" bar keeps white text. The narration-card OK/Next are on
   colored backgrounds (green/red) → white text; Cancel/Replay stay dark. */
.guide-tour-builder .gtb-pickbar,
.guide-tour-builder .gtb-edit-ok,
.guide-tour-builder .gtb-edit-next { color: #fff !important; }
.guide-tour-builder .gtb-edit-ok { background: #2e7d32 !important; }
.guide-tour-builder .gtb-edit-next { background: #c0392b !important; }
.guide-tour-builder .gtb-edit-cancel,
.guide-tour-builder .gtb-edit-replay { color: #2a2a2a !important; background: #fff !important; }
.guide-tour-builder .gtb-mic-btn.is-rec { color: #fff !important; background: #c0392b !important; }
.guide-tour-builder .gtb-mic-x { color: #999 !important; }
</style>
